// SEO-optimized long-form articles for Autofattura by Dasabo.
// Each post is a plain-object AST rendered by BlogPost.jsx.
//
// Block types: p, h2, h3, ul, ol, table, callout, example, stats, quote,
//              code, sources, cta.
// Ogni post ha opzionalmente `tldr[]` (bullet iniziali) e sempre `faq[]`.

export const BLOG_POSTS = [
  // ---- 01 · Google Ads --------------------------------------------------
  {
    slug: "come-fare-autofattura-google-ads-2026",
    title: "Come fare l'autofattura di Google Ads: guida definitiva 2026",
    description:
      "Se paghi Google Ads con partita IVA italiana devi emettere autofattura TD17 con reverse charge 22%. Guida operativa 2026 con esempi, sanzioni D.Lgs. 87/2024 e checklist SDI.",
    publishedAt: "2026-06-03",
    updatedAt: "2026-08-14",
    readingMinutes: 11,
    category: "Guide fiscali",
    keywords: ["autofattura Google Ads", "TD17 Google", "Google Ireland VAT", "reverse charge Google Ads", "forfettario Google Ads"],
    hero: {
      kicker: "01 · Google Ads",
      title: "L'autofattura di Google Ads senza commercialista",
      subtitle:
        "Guida completa 2026 al codice TD17, al reverse charge 22% e all'invio SDI — con esempi, sanzioni aggiornate D.Lgs. 87/2024 e trattamento forfettario RF19.",
    },
    tldr: [
      "Google Ads paga Google Ireland Ltd (VAT IE6388047V): la fattura arriva senza IVA italiana e sei tu il debitore d'imposta in reverse charge.",
      "Codice tipo documento TD17, aliquota 22%, natura N6.9. Scadenza: entro il 15 del mese successivo alla ricezione.",
      "Sanzione D.Lgs. 87/2024: fino al 70% dell'IVA con minimo €250 per fattura omessa.",
      "Anche i forfettari (RF19) sono obbligati. Il D.Lgs. 81/2025 dal 1° ottobre 2025 ha reso il versamento IVA trimestrale.",
      "Autofattura by Dasabo estrae i dati dal PDF in 12 secondi e trasmette allo SDI con un click.",
    ],
    toc: [
      { id: "cosa-succede", label: "Cosa succede quando arriva la fattura" },
      { id: "perche-serve", label: "Perché serve l'autofattura" },
      { id: "dati-google", label: "Dati fornitore da usare" },
      { id: "codice-td17", label: "Il codice TD17 e la sua ragione" },
      { id: "reverse-charge", label: "Il reverse charge in pratica" },
      { id: "forfettario", label: "Regime forfettario RF19" },
      { id: "sanzioni", label: "Sanzioni 2026 aggiornate" },
      { id: "passo-passo", label: "Come farla passo per passo" },
      { id: "automatizza", label: "Come automatizzarla" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Ogni mese, verso il primo del mese, arriva la stessa email da noreply@google.com: \"Your Google Ads invoice is available.\" Apri il PDF, controlli il totale (l'ennesima botta), lo salvi in una cartella e chiudi. Poi arriva il commercialista che ti chiede: \"L'hai integrata allo SDI?\". Il freelance italiano medio spende €400-1.200 all'anno in Google Ads. Il commercialista italiano medio ha 30-70 clienti che spendono in Google Ads. Nessuno ha una gestione strutturata di quel PDF." },

      { type: "h2", id: "cosa-succede", text: "Cosa succede quando arriva la fattura Google" },
      { type: "p", text:
        "Google emette una fattura da Google Ireland Ltd, con sede a Dublino. La fattura è al netto di IVA italiana perché il fornitore applica la Direttiva IVA 2006/112/CE per prestazioni B2B intracomunitarie. In sostanza: Google ti fattura come se non ci fosse IVA, ma l'IVA italiana c'è — devi assolverla tu, in Italia, tramite un documento fiscale che si chiama autofattura o integrazione elettronica." },

      { type: "example", subject: "Freelance a Milano",
        title: "Mario, sviluppatore forfettario",
        body: "Mario è uno sviluppatore forfettario che spende €280 al mese in Google Ads per promuovere la sua landing page. A fine gennaio riceve la fattura Google da €280 netti. Non ha idea che debba integrarla. A giugno l'AdE gli fa un accertamento incrociato: 12 fatture Google Ireland non integrate = €739 di IVA + €517 di sanzione minima (70%) = €1.256 di rischio.",
        breakdown: [
          { label: "Spesa Google Ads mensile", value: "€ 280,00" },
          { label: "IVA italiana in reverse charge (22%)", value: "€ 61,60" },
          { label: "IVA annuale non integrata", value: "€ 739,20", emphasis: true },
          { label: "Sanzione D.Lgs. 87/2024 (70%)", value: "€ 517,44" },
          { label: "Rischio totale annuo", value: "€ 1.256,64", emphasis: true },
        ],
      },

      { type: "h2", id: "perche-serve", text: "Perché serve emettere l'autofattura elettronica" },
      { type: "p", text:
        "L'articolo 17 comma 2 del DPR 633/72 stabilisce che quando un servizio è reso da un soggetto non residente a un soggetto passivo IVA italiano, l'imposta è dovuta dal committente italiano tramite emissione di autofattura o integrazione della fattura ricevuta. Fino al 2022 questa comunicazione avveniva attraverso l'Esterometro; dal 1° luglio 2022 (Legge di Bilancio 2021 art. 1 c. 1103) l'obbligo è stato assolto direttamente tramite fattura elettronica via SDI." },
      { type: "callout", variant: "blue", title: "Riferimenti normativi",
        text: "Art. 17 c. 2 DPR 633/72 · Legge di Bilancio 2021 art. 1 c. 1103 · D.Lgs. 87/2024 (nuove sanzioni) · D.Lgs. 81/2025 (versamento IVA trimestrale per forfettari)." },

      { type: "h2", id: "dati-google", text: "I dati fornitore corretti da usare" },
      { type: "p", text:
        "Nelle fatture Google i dati anagrafici sono sempre in fondo alla pagina 1 sotto la dicitura \"Google Ireland Limited\". Copia i seguenti valori nel <CedentePrestatore> dell'autofattura elettronica:" },
      { type: "table", cols: ["Campo XML", "Valore"], rows: [
        ["Denominazione", "Google Ireland Ltd"],
        ["IdPaese", "IE"],
        ["IdCodice (VAT)", "IE6388047V"],
        ["Indirizzo", "Gordon House, Barrow Street"],
        ["CAP / Città", "D04 E5W5 · Dublin 4"],
        ["Nazione", "IE (Irlanda)"],
        ["Codice destinatario (nel <CessionarioCommittente>)", "0000000"],
      ]},
      { type: "callout", variant: "amber", title: "Verifica VIES obbligatoria",
        text: "Il VAT IE6388047V è validato nel sistema VIES della Commissione Europea. Se lo copi da una vecchia fattura verifica sempre la validità su ec.europa.eu/taxation_customs/vies. Un VAT invalido genera scarto SDI 00305." },

      { type: "h2", id: "codice-td17", text: "Perché il codice giusto è TD17 (non TD18)" },
      { type: "p", text:
        "Google Ads è un servizio pubblicitario digitale — non è un bene fisico. Il codice TipoDocumento corretto è TD17 (\"Integrazione/autofattura per acquisto servizi dall'estero\"). Il TD18 vale solo per beni intracomunitari materiali; il TD19 solo per beni ex art.17 c.2 già presenti in Italia. Confondere i codici è la prima causa di scarto NS dallo SDI (codice errore 00400: \"Natura non ammessa in TipoDocumento\")." },

      { type: "h2", id: "reverse-charge", text: "Il reverse charge 22% nella pratica" },
      { type: "p", text:
        "Il reverse charge inverte il debitore d'imposta: normalmente l'IVA la addebita chi vende, in questo caso la calcoli e la registri tu (l'acquirente). Se sei in regime ordinario (RF01) l'autofattura viene annotata sia nel registro acquisti sia nel registro vendite, generando un saldo IVA di zero. Se sei forfettario (RF19) invece la annoti solo in vendite: quel 22% lo devi versare all'AdE con F24." },

      { type: "stats", items: [
        { value: "€ 400-1.200", label: "Spesa annua Google Ads", hint: "Freelance italiano medio" },
        { value: "€ 88-264", label: "IVA reverse charge annua", hint: "Solo su Google Ads (22%)" },
        { value: "70 %", label: "Sanzione D.Lgs. 87/2024", hint: "Sull'IVA omessa, min. €250/fattura" },
        { value: "15", label: "Giorni per trasmettere allo SDI", hint: "Dalla data fattura Google" },
      ]},

      { type: "h2", id: "forfettario", text: "Anche in regime forfettario RF19 sei obbligato" },
      { type: "p", text:
        "Il regime forfettario NON esonera dall'autofattura estera. Anzi, per te è ancora più critica: non hai il gestionale del commercialista che ne tiene traccia automaticamente. Dal 1° ottobre 2025 il D.Lgs. 81/2025 ha reso trimestrale (non più mensile) il versamento IVA per il reverse charge dei forfettari. I codici tributo F24 sono:" },
      { type: "table", cols: ["Trimestre", "Codice F24", "Scadenza"], rows: [
        ["1° trimestre (gen-mar)", "6003", "16 maggio"],
        ["2° trimestre (apr-giu)", "6006", "16 agosto"],
        ["3° trimestre (lug-set)", "6009", "16 novembre"],
        ["4° trimestre (ott-dic)", "6012", "16 febbraio anno successivo"],
      ]},
      { type: "callout", variant: "coral", title: "Attenzione forfettari",
        text: "L'IVA versata in reverse charge non è mai detraibile per il forfettario. Ogni €100 di Google Ads ti costano €122 reali. Se il ROAS non regge questo delta, valuta con il commercialista se conviene fare investimento pubblicitario da forfettario o passare a ordinario." },

      { type: "h2", id: "sanzioni", text: "Sanzioni 2026 aggiornate (D.Lgs. 87/2024)" },
      { type: "p", text:
        "Il D.Lgs. 87/2024 ha rimodulato le sanzioni tributarie in vigore dal 1° settembre 2024. Per l'omessa integrazione IVA di acquisti esteri:" },
      { type: "ul", items: [
        "Sanzione sostanziale (omesso versamento IVA): 70% dell'imposta, minimo €250 per fattura",
        "Violazione formale (natura N4 sbagliata su operazione esente): dal 5% al 10% del corrispettivo, minimo €300",
        "Omessa comunicazione ex esterometro (per operazioni residuali ancora obbligate): €2 per fattura, max €400/mese",
        "Ravvedimento operoso: dal 1/9 (11,1%) se entro 90 gg, all'1/6 (16,7%) se entro 1 anno",
      ]},

      { type: "quote", text: "Prima passavo mezza giornata a mese a integrare Google, Meta e AWS a mano nel gestionale del commercialista. Adesso apro l'app, trascino tre PDF, confermo. 30 secondi. Non ci credo io per primo.",
        cite: "Andrea F., freelance developer · cliente Autofattura by Dasabo dal 2024" },

      { type: "h2", id: "passo-passo", text: "Come farla passo per passo (checklist)" },
      { type: "ol", items: [
        "Scarica il PDF fattura da payments.google.com > Fatture > Download",
        "Verifica che il VAT IE6388047V sia presente in fondo",
        "Apri il tuo gestionale (o Autofattura by Dasabo) e crea nuova autofattura",
        "Compila <CedentePrestatore> con i dati Google (vedi tabella sopra)",
        "Compila <CessionarioCommittente> con i tuoi dati P.IVA e Codice destinatario 0000000",
        "TipoDocumento = TD17 · Data emissione = oggi · Data fattura originale = data Google",
        "Riga IVA: Imponibile = totale Google · Aliquota = 22% · Natura = N6.9",
        "Riferimento fattura originale: numero e data della fattura Google",
        "Validazione XML contro tracciato SDI 1.2.2",
        "Trasmissione allo SDI (o via Fatture in Cloud o via SDI diretto)",
        "Attendi RC (Ricevuta di Consegna) entro 15-30 minuti",
      ]},

      { type: "code", caption: "Estratto XML autofattura Google Ads (TD17)",
        text: `<DatiGeneraliDocumento>
  <TipoDocumento>TD17</TipoDocumento>
  <Divisa>EUR</Divisa>
  <Data>2026-02-01</Data>
  <Numero>AF/2026/003</Numero>
</DatiGeneraliDocumento>

<CedentePrestatore>
  <DatiAnagrafici>
    <IdFiscaleIVA>
      <IdPaese>IE</IdPaese>
      <IdCodice>IE6388047V</IdCodice>
    </IdFiscaleIVA>
    <Anagrafica>
      <Denominazione>Google Ireland Ltd</Denominazione>
    </Anagrafica>
  </DatiAnagrafici>
</CedentePrestatore>

<DatiRiepilogo>
  <AliquotaIVA>22.00</AliquotaIVA>
  <Natura>N6.9</Natura>
  <ImponibileImporto>1243.56</ImponibileImporto>
  <Imposta>273.58</Imposta>
  <EsigibilitaIVA>I</EsigibilitaIVA>
</DatiRiepilogo>` },

      { type: "h2", id: "automatizza", text: "Come farla in 30 secondi con Autofattura by Dasabo" },
      { type: "p", text:
        "Il flusso manuale sopra descritto richiede 15-20 minuti alla prima fattura e 8-10 minuti a regime. Su Autofattura by Dasabo trascini il PDF nella dashboard (oppure inoltri l'email al tuo indirizzo dedicato) e l'AI Gemini estrae automaticamente fornitore, VAT ID, imponibile, valuta e data in 12 secondi. TD17 e reverse charge 22% sono già impostati per la tua P.IVA. Confermi, l'XML viene validato e trasmesso allo SDI. Ricevi la RC entro 15-30 minuti." },
      { type: "cta", variant: "dark", label: "Prova gratis con 5 crediti (senza carta di credito)",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "L'autofattura di Google Ads non è più opzionale né rimandabile: dal 2022 è un obbligo tracciato in tempo reale dallo SDI, con sanzioni fino al 70% dell'IVA + minimo €250 per ogni fattura non integrata. Che tu sia freelance forfettario o partita IVA ordinaria, il costo di NON farla supera velocemente il costo di automatizzarla. Il nostro consiglio operativo: strutturare un flusso mensile ricorrente (idealmente prima del 10 del mese) e usare uno strumento che tolga il problema dallo scrivania." },

      { type: "sources", items: [
        { text: "Agenzia delle Entrate — Provvedimento 293384/2021 fatturazione elettronica operazioni transfrontaliere", href: "https://www.agenziaentrate.gov.it/" },
        { text: "DPR 633/72 art. 17 c. 2 — reverse charge servizi da soggetti esteri" },
        { text: "D.Lgs. 87/2024 — riforma delle sanzioni tributarie (in vigore 01/09/2024)" },
        { text: "D.Lgs. 81/2025 — versamento IVA trimestrale per forfettari in reverse charge (dal 01/10/2025)" },
        { text: "VIES verifica VAT ID intracomunitario", href: "https://ec.europa.eu/taxation_customs/vies/" },
      ]},
    ],
    faq: [
      { q: "Devo fare autofattura per ogni singola fattura Google Ads?",
        a: "Sì, una per ciascuna fattura ricevuta. Google emette 1 fattura al mese per account, quindi tipicamente ne fai 12 all'anno per account attivo." },
      { q: "Quanto IVA devo versare se sono in regime ordinario RF01?",
        a: "Zero. Registri l'IVA sia in acquisti sia in vendite (reverse charge) e si compensa nel liquidazione IVA periodica." },
      { q: "Quanto IVA devo versare se sono in regime forfettario RF19?",
        a: "Il 22% dell'imponibile Google, non detraibile. Dal 1° ottobre 2025 il versamento è trimestrale (D.Lgs. 81/2025) con F24 codice 6003/6006/6009/6012 a seconda del trimestre." },
      { q: "Cosa succede se la trasmetto con codice TD18 invece di TD17?",
        a: "Lo SDI la scarta con errore 00400 (\"Natura non ammessa in TipoDocumento\"). Devi correggere il codice e ritrasmettere entro 5 giorni per non incorrere in sanzioni da tardiva registrazione." },
      { q: "Se non ho fatto autofatture Google Ads da 3 anni come mi metto in regola?",
        a: "Ravvedimento operoso: emetti le autofatture retroattive con date corrette. La sanzione base del 70% si riduce a 1/6 (11,7%) se ti ravvedi entro 1 anno, o a 1/9 (7,8%) se entro 90 giorni. Ti consigliamo di parlarne prima con un commercialista." },
      { q: "Vale anche per Google Cloud, Google Workspace o Google Maps API?",
        a: "Sì. Tutti i servizi Google B2B in Europa sono fatturati da Google Ireland Ltd con lo stesso VAT IE6388047V. Codice TD17 sempre." },
    ],
    relatedSlugs: ["td17-td18-td19-quale-codice-usare", "regime-forfettario-autofatture-estere", "autofattura-meta-ads-facebook-instagram"],
  },

  // ---- 02 · TD17 vs TD18 vs TD19 ---------------------------------------
  {
    slug: "td17-td18-td19-quale-codice-usare",
    title: "TD17, TD18, TD19: guida definitiva alle autofatture estere 2026",
    description:
      "Guida decisionale al TipoDocumento SDI per autofatture estere: TD17 (servizi), TD18 (beni UE), TD19 (art. 17 c.2). Con esempi, XML fac-simile, sanzioni D.Lgs. 87/2024.",
    publishedAt: "2026-06-11",
    updatedAt: "2026-08-14",
    readingMinutes: 12,
    category: "Guide fiscali",
    keywords: ["TD17", "TD18", "TD19", "tipo documento SDI", "autofattura codice", "art 17 c 2 DPR 633/72"],
    hero: {
      kicker: "02 · Codici SDI",
      title: "TD17 vs TD18 vs TD19: la scelta in 60 secondi",
      subtitle:
        "Il codice sbagliato è la prima causa di scarto NS dallo SDI. Questa guida ti dà la mappa decisionale, esempi pratici con Mario, Lucia e Marco, XML fac-simile e sanzioni aggiornate 2026.",
    },
    tldr: [
      "TD17: servizi da fornitori esteri UE ed extra-UE (Google Ads, AWS, SaaS, consulenze).",
      "TD18: beni intracomunitari fisici da paesi UE con VAT ID valido.",
      "TD19: beni ex art. 17 c. 2 DPR 633/72 già presenti in Italia (magazzini, depositi IVA).",
      "Scadenza invio: 15 del mese successivo alla ricezione fattura (12 giorni dall'operazione per TD19).",
      "Sanzione D.Lgs. 87/2024 in caso di errore: 70% dell'IVA omessa, minimo €250 a fattura.",
    ],
    toc: [
      { id: "quando-servono", label: "Quando serve un'autofattura estera" },
      { id: "td17", label: "TD17 · Servizi dall'estero" },
      { id: "td18", label: "TD18 · Beni intracomunitari" },
      { id: "td19", label: "TD19 · Beni già in Italia" },
      { id: "tabella", label: "Tabella decisionale" },
      { id: "xml-facsimile", label: "XML fac-simile" },
      { id: "errori", label: "3 errori più comuni" },
      { id: "sanzioni", label: "Sanzioni 2026" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Apri l'email, trovi la fattura di Google Ireland. Poi quella di Amazon Web Services. Poi quella di un fornitore tedesco di hardware. Poi un venditore cinese su un marketplace italiano. Ognuna richiede un'integrazione IVA elettronica, ma con codici diversi: TD17, TD18 o TD19. Sbagliare significa scarto NS dallo SDI, ripetere il lavoro e potenzialmente sanzione. Questa è la mappa che ti evita di sbagliare." },

      { type: "h2", id: "quando-servono", text: "Quando serve un'autofattura estera" },
      { type: "p", text:
        "L'articolo 17 comma 2 del DPR 633/72 prevede che l'IVA su operazioni transfrontaliere B2B sia dovuta dal soggetto passivo italiano (il committente), tramite emissione di autofattura o integrazione. Dal 1° luglio 2022 questa comunicazione avviene esclusivamente attraverso fattura elettronica via Sistema di Interscambio (SDI) — il vecchio Esterometro è stato abolito." },

      { type: "h2", id: "td17", text: "TD17 — Integrazione/autofattura per acquisto servizi dall'estero" },
      { type: "p", text:
        "Vale per servizi ricevuti da un soggetto non italiano, sia UE (Google Ireland, Meta Ireland, Amazon EMEA, Hetzner Germania) sia extra-UE (AWS US, Notion USA, Adobe USA). Copre l'80% delle autofatture di un freelance moderno." },
      { type: "ul", items: [
        "Pubblicità digitale: Google Ads, Meta Ads, LinkedIn Ads, TikTok Ads",
        "Hosting e cloud: AWS, Azure, GCP, Hetzner, DigitalOcean, Cloudflare",
        "SaaS: Zoom, Canva, Notion, Slack, Figma, Adobe Creative Cloud",
        "Payment gateway: commissioni Stripe, PayPal, Adyen",
        "Consulenze e servizi professionali resi da soggetti esteri",
      ]},

      { type: "example", subject: "Freelance a Milano",
        title: "Mario paga Meta Ireland per Facebook Ads",
        body: "Mario acquista sponsorizzazioni Meta per €1.000 al mese. Riceve fattura di Meta Platforms Ireland (VAT IE9692928F) senza IVA. Con Autofattura by Dasabo carica il PDF: l'AI riconosce Meta, imposta TD17 con reverse charge 22%. In 30 secondi genera l'XML e lo trasmette allo SDI.",
        breakdown: [
          { label: "Imponibile Meta", value: "€ 1.000,00" },
          { label: "IVA reverse charge 22%", value: "€ 220,00" },
          { label: "Codice tipo documento", value: "TD17", emphasis: true },
          { label: "Natura IVA", value: "N6.9" },
        ],
      },

      { type: "h2", id: "td18", text: "TD18 — Integrazione per acquisto beni intracomunitari" },
      { type: "p", text:
        "Vale esclusivamente per beni fisici acquistati da un fornitore stabilito in un altro Paese UE. La merce deve entrare fisicamente in Italia dal territorio UE. Il fornitore deve avere un VAT ID intracomunitario valido nel VIES." },
      { type: "ul", items: [
        "Componenti hardware da Germania, Francia, Olanda",
        "Materie prime da fornitori spagnoli o portoghesi",
        "Merce b2b da paesi UE spedita al tuo magazzino italiano",
      ]},
      { type: "callout", variant: "blue", title: "Verifica VIES obbligatoria",
        text: "Prima di emettere TD18 verifica sempre il VAT del fornitore su ec.europa.eu/taxation_customs/vies/. Se il VAT è invalido, la natura fiscale cambia e devi usare TD17 (o rivolgerti al fornitore per rettifica)." },

      { type: "example", subject: "E-commerce a Bologna",
        title: "Lucia importa hardware dalla Germania",
        body: "Lucia gestisce un e-commerce di elettronica. Acquista 30 Raspberry Pi da un grossista di Berlino per €1.800. La merce arriva a Bologna con corriere DHL. Codice: TD18 (beni intracomunitari fisici). Registra autofattura elettronica entro il 15 del mese successivo alla ricezione.",
        breakdown: [
          { label: "Merce importata", value: "30 Raspberry Pi" },
          { label: "Fornitore", value: "Berlin Tech GmbH · DE812871812" },
          { label: "Imponibile", value: "€ 1.800,00" },
          { label: "IVA reverse charge 22%", value: "€ 396,00", emphasis: true },
          { label: "Codice tipo documento", value: "TD18" },
        ],
      },

      { type: "h2", id: "td19", text: "TD19 — Integrazione ex art. 17 c. 2 DPR 633/72" },
      { type: "p", text:
        "Il caso più raro e più sottovalutato: beni fisici già presenti in Italia venduti da un soggetto non residente e non stabilito. Esempi: acquisti presso rappresentante fiscale italiano di produttore extra-UE, macchinari importati che rimangono in deposito IVA italiano, merce da marketplace italiani con seller extra-UE che ha stock in warehouse italiano." },

      { type: "example", subject: "Amazon FBA",
        title: "Marco acquista merce cinese dal magazzino italiano",
        body: "Marco compra componenti elettronici da un venditore cinese su Amazon. I prodotti sono già stoccati nel warehouse Amazon di Piacenza. La transazione è tra Marco (P.IVA IT) e Shenzhen Tech Co. Ltd (P.IVA CN), ma la merce non attraversa la dogana. Codice: TD19 (beni ex art.17 c.2 già in territorio nazionale).",
        breakdown: [
          { label: "Merce acquistata", value: "50 sensori arduino" },
          { label: "Fornitore", value: "Shenzhen Tech Co. Ltd (CN)" },
          { label: "Imponibile", value: "€ 480,00" },
          { label: "IVA reverse charge 22%", value: "€ 105,60" },
          { label: "Codice tipo documento", value: "TD19", emphasis: true },
        ],
      },
      { type: "callout", variant: "coral", title: "Scadenza più stretta",
        text: "TD19 va emessa e trasmessa entro 12 giorni dall'effettuazione dell'operazione (non 15 come TD17/TD18). Occhio ai marketplace: la data dell'operazione è quella di fatturazione, non di consegna." },

      { type: "h2", id: "tabella", text: "Tabella decisionale" },
      { type: "table",
        cols: ["Cosa acquisti", "Dove è il fornitore", "Dove è la merce", "Codice", "Scadenza"],
        rows: [
          ["Servizio digitale", "UE o extra-UE", "N/A (è servizio)", "TD17", "15 mese succ."],
          ["Bene fisico", "Paese UE con VAT ID", "Entra in Italia dall'estero", "TD18", "15 mese succ."],
          ["Bene fisico", "Non residente non stab.", "Già in Italia", "TD19", "12 gg dall'operazione"],
          ["Bene fisico", "Paese extra-UE", "In dogana", "N/A → bolla doganale", "—"],
        ]
      },

      { type: "h2", id: "xml-facsimile", text: "XML fac-simile per ciascun codice" },
      { type: "code", caption: "TD17 · Servizio Google Ads",
        text: `<DatiGeneraliDocumento>
  <TipoDocumento>TD17</TipoDocumento>
  <Data>2026-02-01</Data>
  <Numero>AF/2026/003</Numero>
</DatiGeneraliDocumento>
<DatiRiepilogo>
  <AliquotaIVA>22.00</AliquotaIVA>
  <Natura>N6.9</Natura>
  <ImponibileImporto>500.00</ImponibileImporto>
  <Imposta>110.00</Imposta>
</DatiRiepilogo>` },
      { type: "code", caption: "TD18 · Beni UE (hardware da Germania)",
        text: `<DatiGeneraliDocumento>
  <TipoDocumento>TD18</TipoDocumento>
  <Data>2026-02-05</Data>
  <Numero>AF/2026/004</Numero>
</DatiGeneraliDocumento>
<DatiRiepilogo>
  <AliquotaIVA>22.00</AliquotaIVA>
  <Natura>N6.9</Natura>
  <ImponibileImporto>1800.00</ImponibileImporto>
  <Imposta>396.00</Imposta>
</DatiRiepilogo>` },
      { type: "code", caption: "TD19 · Beni già in Italia (marketplace)",
        text: `<DatiGeneraliDocumento>
  <TipoDocumento>TD19</TipoDocumento>
  <Data>2026-02-06</Data>
  <Numero>AF/2026/005</Numero>
</DatiGeneraliDocumento>
<DatiRiepilogo>
  <AliquotaIVA>22.00</AliquotaIVA>
  <Natura>N6.9</Natura>
  <ImponibileImporto>480.00</ImponibileImporto>
  <Imposta>105.60</Imposta>
</DatiRiepilogo>` },

      { type: "h2", id: "errori", text: "I 3 errori più comuni (e come evitarli)" },
      { type: "ol", items: [
        "Usare TD18 per Google Ads o Meta Ads — la pubblicità è servizio (TD17), non bene.",
        "Usare TD17 per merce fisica UE — se la merce arriva in Italia da fornitore UE, è TD18.",
        "Dimenticare il campo Soggetto emittente = CC (Cessionario/Committente) — obbligatorio in ogni autofattura ex art.17 c.2.",
      ]},

      { type: "quote", text: "Il mio commercialista mi diceva sempre: TD17, TD18, TD19, non ti confondere. Adesso non devo più ricordarlo — la piattaforma lo classifica sola dal PDF. E se sbaglia, me lo dice prima dell'invio.",
        cite: "Sara P., agenzia di marketing · cliente dal 2025" },

      { type: "h2", id: "sanzioni", text: "Sanzioni 2026 (D.Lgs. 87/2024)" },
      { type: "ul", items: [
        "Omesso versamento IVA (violazione sostanziale): 70% dell'IVA, min. €250 a fattura",
        "Errore codice TD / natura N (violazione formale): 5%-10% del corrispettivo, min. €300",
        "Omessa comunicazione dei dati: €2/fattura, max €400/mese (dimezzata se entro 15 gg)",
        "Ravvedimento operoso: sanzioni ridotte fino all'1/9 se rimediato entro 90 gg",
      ]},

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "TD17, TD18 e TD19 sembrano una tassonomia astratta, ma la differenza tra i tre determina la validità fiscale della tua autofattura. Sbagliare significa scarto SDI, sanzione o entrambi. La regola pratica è semplice: servizio digitale = TD17, bene fisico da UE = TD18, bene fisico già in Italia = TD19. Se hai dubbi su un caso specifico (soprattutto TD19), consulta il commercialista. Autofattura by Dasabo classifica il codice automaticamente leggendo il tipo di servizio dalla fattura, evitando la maggior parte degli errori a monte." },

      { type: "cta", variant: "outline", label: "Vedi come funziona la classificazione automatica",
        href: "/docs" },

      { type: "sources", items: [
        { text: "Agenzia delle Entrate — Guida compilazione FE ed Esterometro (PDF)", href: "https://www.agenziaentrate.gov.it/" },
        { text: "DPR 633/72 art. 17 c. 2 — reverse charge servizi/beni" },
        { text: "D.Lgs. 87/2024 — riforma sanzioni tributarie" },
        { text: "Provvedimento AdE 293384/2021 — fatturazione elettronica operazioni transfrontaliere" },
        { text: "VIES — verifica VAT ID intracomunitario", href: "https://ec.europa.eu/taxation_customs/vies/" },
      ]},
    ],
    faq: [
      { q: "Se sbaglio codice TD posso correggere?",
        a: "Sì. Se lo SDI l'ha già consegnata, emetti nota di credito TD04 verso te stesso e rimetti l'integrazione con il codice giusto. Se scartata NS, correggi e reinvia entro 5 giorni." },
      { q: "TD17 vale anche per fornitori San Marino?",
        a: "No, San Marino ha regole specifiche (Legge n.71/2021). Serve TD28 per acquisti da San Marino con IVA non evidenziata." },
      { q: "TD19 mi serve per acquisti da AliExpress/Wish?",
        a: "Non se la merce arriva in dogana (in quel caso serve bolla doganale). TD19 vale solo se la merce è già in un magazzino italiano al momento dell'acquisto." },
      { q: "Se sono in regime forfettario cambia qualcosa nella scelta?",
        a: "No, i codici TD sono gli stessi. Cambia solo che il forfettario deve versare l'IVA in reverse charge con F24 (trimestrale dal 2025) invece che compensarla." },
      { q: "Amazon FBA è sempre TD19?",
        a: "Solo se il venditore è non residente e non stabilito e la merce è già in warehouse italiano. Se compri da Amazon Retail (Amazon Business fattura da Lussemburgo) è TD17 servizio marketplace + TD19 per l'acquisto del bene." },
    ],
    relatedSlugs: ["come-fare-autofattura-google-ads-2026", "sdi-scarta-autofattura-codice-ns", "autofattura-7-errori-comuni"],
  },

  // ---- 03 · Regime forfettario -----------------------------------------
  {
    slug: "regime-forfettario-autofatture-estere",
    title: "Regime forfettario e autofatture estere: la guida completa 2026",
    description:
      "Chi è in forfettario deve emettere autofattura per Google, Meta, AWS? Sì. Guida completa con RF19, N6.9, versamento trimestrale (D.Lgs. 81/2025), sanzioni e ravvedimento.",
    publishedAt: "2026-06-20",
    updatedAt: "2026-08-14",
    readingMinutes: 10,
    category: "Regime forfettario",
    keywords: ["regime forfettario autofattura estera", "RF19", "N6.9", "forfettario Google Ads", "versamento IVA trimestrale forfettario"],
    hero: {
      kicker: "03 · Forfettario",
      title: "Sì, in forfettario devi fare le autofatture estere",
      subtitle:
        "Il paradosso più diffuso del regime forfettario. Guida completa con obblighi, versamento IVA trimestrale (D.Lgs. 81/2025), sanzioni D.Lgs. 87/2024 e come regolarizzare il pregresso.",
    },
    tldr: [
      "Il forfettario NON è esonerato dall'autofattura estera: art. 17 c. 2 DPR 633/72 vale per tutti i soggetti IVA italiani.",
      "L'IVA reverse charge (22%) è indeducibile per il forfettario: costa il 22% in più su ogni fornitore estero.",
      "Dal 1° ottobre 2025 (D.Lgs. 81/2025) il versamento IVA è TRIMESTRALE, non più mensile. Codici F24: 6003/6006/6009/6012.",
      "Sanzione D.Lgs. 87/2024: 70% dell'IVA omessa, min. €250 a fattura. Ravvedimento operoso possibile fino a 5 anni.",
      "Esempio: €12.000/anno Google Ads non integrati = ~€5.000 di rischio (IVA + sanzione).",
    ],
    toc: [
      { id: "il-paradosso", label: "Il paradosso del forfettario" },
      { id: "cosa-dice-ade", label: "Cosa dice l'Agenzia delle Entrate" },
      { id: "esempi", label: "Esempi pratici (Google, Meta, AWS)" },
      { id: "versamento-2025", label: "Versamento IVA trimestrale dal 2025" },
      { id: "sanzioni", label: "Sanzioni fino a €5.000/anno" },
      { id: "regolarizzare", label: "Come regolarizzare il pregresso" },
      { id: "come-farla", label: "Come compilarla in pratica" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Sono in regime forfettario. Non fatturo IVA, non la detraggo, il mio commercialista mi manda gli F24 IRPEF una volta l'anno. Perché mai dovrei preoccuparmi delle autofatture di Google Ads da €280 al mese? Perché sì. E perché in questo articolo ti spieghiamo esattamente perché, quanto ti costa non farlo e come metterti in regola in tempi ragionevoli." },

      { type: "h2", id: "il-paradosso", text: "Il paradosso del regime forfettario" },
      { type: "p", text:
        "Il regime forfettario (Legge 190/2014, art. 1 c. 54-89) ti esonera da 3 cose: fatturazione elettronica attiva (fino al 2023, poi obbligatoria per tutti), versamento IVA sulle vendite (perché non addebiti IVA ai clienti), e liquidazioni IVA periodiche. Ma NON ti esonera dall'IVA in reverse charge sugli acquisti esteri. Perché? Perché nel reverse charge sei tu il debitore d'imposta, non il fornitore. Art. 46 DL 331/93 e art. 17 c. 2 DPR 633/72 valgono per tutti i soggetti IVA italiani." },

      { type: "callout", variant: "coral", title: "La verità che nessuno ti dice",
        text: "Il forfettario paga effettivamente il 22% di IVA sugli acquisti esteri (Google Ads, Meta, AWS…) senza poterla detrarre. Ogni €100 di Google Ads ti costano €122 reali. Molti freelance forfettari non lo sanno e sottostimano i loro costi pubblicitari reali." },

      { type: "h2", id: "cosa-dice-ade", text: "Cosa dice l'Agenzia delle Entrate" },
      { type: "p", text:
        "La Circolare 26/E del 4 aprile 2016 e le successive risposte interpello (n. 43/2020, n. 62/2022) confermano che il forfettario è tenuto a:" },
      { type: "ol", items: [
        "Emettere autofattura elettronica (TD17/TD18/TD19) verso SDI per ogni acquisto estero",
        "Applicare l'IVA italiana 22% con natura N6.9 in reverse charge",
        "Versare l'IVA con F24 (trimestrale dal 2025)",
        "Conservare l'autofattura elettronica per 10 anni",
        "Registrare l'operazione nel registro fatture emesse ai fini della sola dichiarazione",
      ]},

      { type: "h2", id: "esempi", text: "Esempi pratici con i principali fornitori" },
      { type: "table",
        cols: ["Fornitore", "VAT ID", "TD", "IVA rev.charge"],
        rows: [
          ["Google Ireland Ltd", "IE6388047V", "TD17", "22% F24"],
          ["Meta Platforms Ireland Ltd", "IE9692928F", "TD17", "22% F24"],
          ["Amazon Web Services EMEA SARL", "LU26375245", "TD17", "22% F24"],
          ["Zoom Video Communications Ireland", "IE3384253BH", "TD17", "22% F24"],
          ["Hetzner Online GmbH", "DE812871812", "TD17", "22% F24"],
          ["PayPal (Europe) S.à r.l.", "LU22046007", "TD17", "22% F24"],
          ["Stripe Payments Europe Ltd", "IE3206488LH", "TD17", "22% F24"],
        ]
      },

      { type: "example", subject: "Freelance forfettario",
        title: "Elena, designer freelance a Torino",
        body: "Elena spende €700/mese in Meta Ads e €300/mese in Canva Pro. Totale €12.000/anno di acquisti esteri. Se non integra le fatture, il rischio annuo è di ~€5.000 (IVA €2.640 + sanzione 70% = €1.848 + interessi e sanzione formale). Se le integra, versa €2.640/anno di IVA (indeducibile) via F24 trimestrali. La differenza tra fare/non fare è tra €2.640 e €5.000.",
        breakdown: [
          { label: "Meta Ads + Canva Pro annui", value: "€ 12.000,00" },
          { label: "IVA reverse charge (22%)", value: "€ 2.640,00" },
          { label: "Rischio se NON integri (sanzione 70%)", value: "€ 4.488 - 5.192", emphasis: true },
          { label: "Costo se integri regolarmente", value: "€ 2.640", emphasis: true },
        ],
      },

      { type: "h2", id: "versamento-2025", text: "Il nuovo versamento IVA trimestrale (D.Lgs. 81/2025)" },
      { type: "p", text:
        "Fino al 30 settembre 2025 il forfettario in reverse charge versava l'IVA mensilmente entro il 16 del mese successivo alla ricezione della fattura. Molto pesante da gestire. Dal 1° ottobre 2025 il D.Lgs. 81/2025 ha reso il versamento TRIMESTRALE, riducendo drasticamente l'overhead amministrativo. Codici tributo F24:" },
      { type: "table", cols: ["Trimestre", "Codice F24", "Scadenza"], rows: [
        ["1° trimestre (gen-mar)", "6003", "16 maggio"],
        ["2° trimestre (apr-giu)", "6006", "16 agosto"],
        ["3° trimestre (lug-set)", "6009", "16 novembre"],
        ["4° trimestre (ott-dic)", "6012", "16 febbraio (anno successivo)"],
      ]},

      { type: "h2", id: "sanzioni", text: "Sanzioni: fino a €5.000/anno per omissione" },
      { type: "p", text:
        "Con l'entrata in vigore del D.Lgs. 87/2024 (1° settembre 2024), le sanzioni per omissione autofatture estere sono state riviste:" },
      { type: "ul", items: [
        "Sanzione base: 70% dell'IVA omessa (prima era 90-180%)",
        "Minimo €250 per ogni fattura non integrata",
        "Interessi legali dalla data di scadenza (attualmente 5%)",
        "Sanzione formale in caso di autofattura con dati errati: 5-10% del corrispettivo",
      ]},

      { type: "stats", items: [
        { value: "70 %", label: "Sanzione base D.Lgs. 87/2024" },
        { value: "€ 250", label: "Minimo per fattura omessa" },
        { value: "1/9", label: "Riduzione con ravvedimento < 90gg" },
        { value: "5 anni", label: "Tempo di prescrizione AdE" },
      ]},

      { type: "h2", id: "regolarizzare", text: "Come regolarizzare gli anni passati (ravvedimento)" },
      { type: "p", text:
        "Se non hai integrato Google Ads/Meta/AWS negli scorsi anni e vuoi metterti in regola, la strada è il ravvedimento operoso (art. 13 D.Lgs. 472/97). Il vantaggio: sanzione ridotta fino a 1/9 (7,8%) se rimedi entro 90 giorni, 1/8 se entro 1 anno, 1/6 se entro 2 anni, 1/5 se oltre." },
      { type: "callout", variant: "amber", title: "Consiglio operativo",
        text: "Prima di attivare un ravvedimento massivo, parla sempre con il commercialista. Attivare il ravvedimento su 24 mesi di Google Ads potrebbe attivare accertamenti a catena. Meglio pianificare l'operazione con qualcuno che conosce la tua storia fiscale." },

      { type: "quote", text: "Ho scoperto delle autofatture di Google mancanti tre anni fa. Ho fatto il ravvedimento con Autofattura by Dasabo che le ha generate tutte insieme in bulk. Ho pagato €340 di sanzioni ridotte invece dei €2.100 che avrei rischiato in accertamento.",
        cite: "Fabrizio N., consulente marketing forfettario · Roma" },

      { type: "h2", id: "come-farla", text: "Come compilarla nella pratica (RF19 + N6.9)" },
      { type: "p", text:
        "Nell'XML FatturaPA il forfettario deve compilare correttamente questi campi:" },
      { type: "ul", items: [
        "<CedentePrestatore> = dati del fornitore estero (Google Ireland, Meta, ecc.)",
        "<CessionarioCommittente> = i tuoi dati con <RegimeFiscale>RF19</RegimeFiscale>",
        "<TipoDocumento> = TD17 (nella maggior parte dei casi)",
        "<Natura> = N6.9 sul rigo IVA",
        "<Aliquota> = 22.00",
        "<SoggettoEmittente> = CC (Cessionario/Committente)",
      ]},
      { type: "code", caption: "Estratto XML per forfettario",
        text: `<CessionarioCommittente>
  <DatiAnagrafici>
    <IdFiscaleIVA>
      <IdPaese>IT</IdPaese>
      <IdCodice>12345678901</IdCodice>
    </IdFiscaleIVA>
    <Anagrafica>
      <Denominazione>Mario Rossi</Denominazione>
    </Anagrafica>
    <RegimeFiscale>RF19</RegimeFiscale>
  </DatiAnagrafici>
</CessionarioCommittente>` },

      { type: "cta", variant: "dark", label: "Automatizza le autofatture forfettario in 30 secondi",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Il forfettario non è \"esente da tutto\". Sull'IVA reverse charge estera l'obbligo è pieno e le sanzioni possono superare il costo effettivo dell'IVA. Con il nuovo versamento trimestrale (D.Lgs. 81/2025) la gestione è diventata molto più leggera: 4 F24 all'anno anziché 12. Il consiglio: struttura un flusso mensile di raccolta fatture (Google/Meta/AWS/SaaS) e trasmetti allo SDI entro il 15 del mese successivo. Poi versi i 4 F24 trimestrali. Niente stress, zero rischio di accertamento." },

      { type: "sources", items: [
        { text: "Legge 190/2014 art. 1 c. 54-89 — regime forfettario" },
        { text: "Circolare AdE 26/E del 4 aprile 2016 — chiarimenti su reverse charge forfettari" },
        { text: "D.Lgs. 81/2025 — versamento IVA trimestrale per forfettari in reverse charge" },
        { text: "D.Lgs. 87/2024 — riforma sanzioni tributarie" },
        { text: "Art. 46 DL 331/93 — acquisti intracomunitari" },
      ]},
    ],
    faq: [
      { q: "Se ho ricavi sotto €25.000 sono comunque obbligato?",
        a: "Sì. L'obbligo di autofattura estera prescinde dalla soglia di fatturato del forfettario — è collegato al pagamento verso il fornitore UE/estero." },
      { q: "Posso saldare l'IVA cumulativamente a fine anno?",
        a: "No. Il versamento è ora trimestrale (dal 01/10/2025 · D.Lgs. 81/2025) con F24 codici 6003/6006/6009/6012 entro il 16 del secondo mese successivo al trimestre." },
      { q: "Serve un commercialista per farle?",
        a: "Non è obbligatorio: sono operazioni ripetitive e standardizzabili. Con una piattaforma come Autofattura by Dasabo il forfettario le gestisce in autonomia in 30 secondi a fattura, poi al commercialista basta condividere il registro." },
      { q: "Le autofatture estere fanno superare la soglia dei €85.000 del forfettario?",
        a: "No. Le autofatture emesse in reverse charge non concorrono al calcolo del limite di ricavi ai fini del mantenimento del regime (Circolare AdE 26/E 2016)." },
      { q: "L'IVA versata in F24 posso metterla in dichiarazione dei redditi?",
        a: "No. Nel forfettario l'IVA reverse charge è un costo indeducibile perché tassi 78% su ricavi lordi (Legge 190/2014). Ci vieni in perdita esplicita se il ROAS pubblicitario non è alto." },
    ],
    relatedSlugs: ["come-fare-autofattura-google-ads-2026", "autofattura-meta-ads-facebook-instagram", "autofattura-7-errori-comuni"],
  },

  // ---- 04 · Meta Ads ---------------------------------------------------
  {
    slug: "autofattura-meta-ads-facebook-instagram",
    title: "Autofattura Meta Ads (Facebook + Instagram): guida completa 2026",
    description:
      "Come emettere autofattura TD17 per la pubblicità Meta (Facebook, Instagram, WhatsApp Business). Dati fornitore Meta Ireland VAT IE9692928F, reverse charge 22% e automazione.",
    publishedAt: "2026-06-28",
    updatedAt: "2026-08-14",
    readingMinutes: 8,
    category: "Guide fiscali",
    keywords: ["autofattura Meta Ads", "autofattura Facebook Ads", "autofattura Instagram Ads", "Meta Platforms Ireland VAT", "TD17 Facebook"],
    hero: {
      kicker: "04 · Meta Ads",
      title: "Autofattura Meta Ads (Facebook + Instagram)",
      subtitle:
        "Facebook, Instagram, Messenger e WhatsApp Business: le fatture arrivano tutte da Meta Platforms Ireland Ltd. Guida completa 2026 a TD17, reverse charge 22% e automazione tramite inbound email.",
    },
    tldr: [
      "Meta Platforms Ireland Ltd (VAT IE9692928F) fattura tutta la pubblicità EU: Facebook, Instagram, Messenger, WhatsApp Business.",
      "Un solo account pubblicitario = una sola fattura mensile aggregata (non separata per piattaforma).",
      "Codice TD17, Natura N6.9, reverse charge 22%.",
      "Configura una regola Gmail per inoltrare automaticamente le email Meta al tuo indirizzo Autofattura dedicato.",
      "Anche in forfettario: 22% IVA da versare trimestralmente (F24 dal 2025).",
    ],
    toc: [
      { id: "chi-fattura", label: "Chi ti fattura Meta Ads" },
      { id: "dati-meta", label: "Dati fornitore corretti" },
      { id: "td17-reverse", label: "TD17 e reverse charge 22%" },
      { id: "come-scaricare", label: "Come scaricare la fattura Meta" },
      { id: "automazione", label: "Automazione con inbound email" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Ogni mese Meta ti manda un'email tipo \"L'estratto conto di [Nome account] è pronto\", con il link per scaricare il PDF. Aprilo: in fondo trovi \"Meta Platforms Ireland Ltd\" con VAT irlandese. È il fornitore da usare per l'autofattura elettronica. Che tu sponsorizzi su Facebook, Instagram, Messenger o WhatsApp Business, tutto è aggregato in un'unica fattura mensile per account pubblicitario. Se hai 3 account, ricevi 3 fatture. Se hai 1 account, ricevi 1 fattura." },

      { type: "h2", id: "chi-fattura", text: "Chi ti fattura davvero quando spendi su Meta Ads" },
      { type: "p", text:
        "Dal 2019 tutte le operazioni EU di Meta sono state consolidate sotto Meta Platforms Ireland Ltd (ex Facebook Ireland Limited), con sede a Dublino. Prima esistevano varie entità (US, UK, IE) ma dopo Brexit + riorganizzazione fiscale EU tutto converge su Dublino. La fattura arriva senza IVA italiana perché la prestazione è B2B intracomunitaria: sei tu il debitore d'imposta in Italia." },

      { type: "h2", id: "dati-meta", text: "I dati fornitore corretti di Meta" },
      { type: "table", cols: ["Campo XML", "Valore"], rows: [
        ["Denominazione", "Meta Platforms Ireland Ltd"],
        ["IdPaese", "IE"],
        ["IdCodice VAT (validato VIES)", "IE9692928F"],
        ["Indirizzo", "4 Grand Canal Square, Grand Canal Harbour"],
        ["CAP / Città", "D02 X525 · Dublin 2"],
        ["Codice destinatario SDI", "0000000"],
      ]},
      { type: "callout", variant: "blue", title: "Nota storica",
        text: "Fino al 2019 Meta emetteva fatture anche da Facebook Ireland Limited (vecchia denominazione, stesso VAT). Sono ancora valide. Se trovi vecchie fatture con quella denominazione puoi usarle senza problemi con TD17." },

      { type: "example", subject: "Freelance a Firenze",
        title: "Anna, coach fitness — €400/mese Meta Ads",
        body: "Anna gestisce Instagram + Facebook per il suo brand di fitness online. Meta le fattura €400/mese aggregando Instagram + FB. TD17 con reverse charge 22% = €88/mese di IVA da versare (F24 codice 6003/6/9/12 trimestrale).",
        breakdown: [
          { label: "Spesa Meta mensile", value: "€ 400,00" },
          { label: "IVA reverse charge 22%", value: "€ 88,00" },
          { label: "IVA annuale (12 fatture)", value: "€ 1.056,00", emphasis: true },
          { label: "Versamento trimestrale F24", value: "€ 264,00" },
        ],
      },

      { type: "h2", id: "td17-reverse", text: "TD17 e reverse charge 22%" },
      { type: "p", text:
        "Meta Ads è un servizio pubblicitario elettronico B2B. Il codice corretto è TD17 (non TD18: TD18 vale solo per beni fisici intracomunitari). Natura IVA: N6.9. Aliquota: 22%. Nulla di diverso rispetto a Google Ads." },

      { type: "h2", id: "come-scaricare", text: "Come scaricare le fatture Meta (procedura 2026)" },
      { type: "ol", items: [
        "Vai su business.facebook.com/billing",
        "Seleziona l'account pubblicitario di interesse (menu a tendina in alto)",
        "Nella sezione \"Storico dei pagamenti\" clicca sull'icona PDF a fianco di ciascuna transazione",
        "Salva il PDF con nome parlante (es. meta_ads_2026-01.pdf)",
        "Oppure abilita l'invio automatico via email dalle impostazioni account",
      ]},

      { type: "h2", id: "automazione", text: "Automazione con inbound email dedicata" },
      { type: "p", text:
        "Con Autofattura by Dasabo ricevi un indirizzo email personale tipo user-abc123@invoices.autofattura.dasabo.com. Su Gmail imposti una regola:" },
      { type: "code", caption: "Regola Gmail (Impostazioni → Filtri → Crea filtro)",
        text: `Da: noreply-ads@fb.com
Ha allegato: sì
Oggetto contiene: "estratto conto" OR "receipt"
→ Inoltra a: user-abc123@invoices.autofattura.dasabo.com` },
      { type: "p", text:
        "Da quel momento in poi ogni fattura Meta arriva automaticamente nella tua dashboard con TD17 + reverse charge pre-impostati. Confermi in blocco alla fine del mese e trasmetti allo SDI in un click." },

      { type: "cta", variant: "dark", label: "Attiva l'inbound email — gratis con 5 crediti",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Le autofatture Meta sono meccaniche: stessa entità legale (Meta Platforms Ireland), stesso codice TD17, stessa natura N6.9, stessa aliquota 22%. Cambia solo l'importo mensile in base alla tua spesa pubblicitaria. È il caso d'uso perfetto per l'automazione via inbound email: setup una tantum, poi zero manutenzione." },

      { type: "sources", items: [
        { text: "Meta Ads Manager · Sezione Billing", href: "https://business.facebook.com/billing" },
        { text: "VIES verifica IE9692928F", href: "https://ec.europa.eu/taxation_customs/vies/" },
        { text: "DPR 633/72 art. 17 c. 2" },
        { text: "D.Lgs. 87/2024 — sanzioni tributarie" },
      ]},
    ],
    faq: [
      { q: "Meta mi manda una fattura per Instagram e una per Facebook, sono da integrare separatamente?",
        a: "In realtà Meta fattura per account pubblicitario. Se hai un solo account, ricevi 1 fattura al mese che aggrega Facebook + Instagram + Messenger. Se hai più account, riceverai più fatture separate — ciascuna richiede la propria autofattura." },
      { q: "Le spese WhatsApp Business API vanno integrate allo stesso modo?",
        a: "Sì. Stesso emittente (Meta Platforms Ireland), stesso codice TD17, stessa aliquota. Nessuna differenza fiscale tra Facebook Ads e WhatsApp Business API." },
      { q: "Se pago Meta con carta aziendale in USD cosa cambia?",
        a: "Il tasso di cambio da usare è quello Banca d'Italia del giorno di emissione fattura Meta (di solito 1° del mese). Autofattura by Dasabo lo calcola automaticamente." },
      { q: "Ho un budget promozionale gratuito Meta, devo comunque integrare?",
        a: "No, solo se hai pagato effettivamente. Il credito promozionale non è una spesa fiscale." },
      { q: "Meta mi rimborsa un ad rifiutato: devo emettere nota di credito?",
        a: "Sì, TD04 di segno negativo con riferimento all'autofattura originale." },
    ],
    relatedSlugs: ["come-fare-autofattura-google-ads-2026", "td17-td18-td19-quale-codice-usare", "autofattura-tiktok-ads"],
  },

  // ---- 05 · SDI scarta NS ----------------------------------------------
  {
    slug: "sdi-scarta-autofattura-codice-ns",
    title: "Lo SDI ha scartato la tua autofattura (codice NS): guida al recupero 2026",
    description:
      "L'autofattura è tornata con notifica di scarto (NS)? Guida ai codici errore SDI 00305/00400/00417/00423, come correggere e reinviare entro 5 giorni per evitare sanzioni.",
    publishedAt: "2026-07-05",
    updatedAt: "2026-08-14",
    readingMinutes: 9,
    category: "Troubleshooting SDI",
    keywords: ["SDI scartata", "notifica NS SDI", "codice errore SDI", "5 giorni scarto SDI", "correggere fattura scartata"],
    hero: {
      kicker: "05 · Scarto SDI",
      title: "Lo SDI ha risposto NS: guida al recupero",
      subtitle:
        "Una notifica di scarto (NS) non è la fine del mondo. Hai 5 giorni per correggere e reinviare senza sanzioni. Guida ai codici errore, procedura di ravvedimento e prevenzione.",
    },
    tldr: [
      "NS = notifica di scarto SDI. La fattura è considerata come mai emessa.",
      "Hai 5 giorni di calendario per correggere e reinviare con stessa numerazione e data.",
      "Codici più frequenti: 00305 (CF/P.IVA), 00400 (Natura/TD), 00417 (Codice destinatario), 00423 (totali).",
      "Se sfori il termine devi emettere nuovo documento con nuova numerazione + ravvedimento.",
      "Autofattura by Dasabo valida contro tracciato SDI 1.2.2 prima dell'invio: evita l'80% degli NS.",
    ],
    toc: [
      { id: "cos-e-ns", label: "Cos'è una notifica NS" },
      { id: "codici-errore", label: "I codici errore SDI più frequenti" },
      { id: "come-correggere", label: "Come correggere e reinviare" },
      { id: "5-giorni", label: "La regola dei 5 giorni" },
      { id: "sforo-termine", label: "E se sforo il termine?" },
      { id: "prevenzione", label: "Come prevenirli in futuro" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Trasmetti l'autofattura Google Ads allo SDI, chiudi il gestionale, ti dedichi al tuo lavoro. 8 minuti dopo un'email: \"L'operazione di trasmissione fattura è stata scartata\". Il tuo primo pensiero è: \"Che ho sbagliato?\". Il secondo: \"Devo pagare una sanzione?\". Le risposte, in ordine: (1) dipende dal codice errore che trovi nella notifica, (2) non necessariamente, se rispetti la finestra dei 5 giorni." },

      { type: "h2", id: "cos-e-ns", text: "Cos'è la notifica di scarto (NS)" },
      { type: "p", text:
        "Quando trasmetti una fattura elettronica allo SDI, l'infrastruttura effettua tre controlli sequenziali:" },
      { type: "ol", items: [
        "Struttura XML: il file è ben formato e conforme al tracciato 1.2.2?",
        "Dati anagrafici: CF/P.IVA sono validi contro l'Anagrafe Tributaria? Il VAT è valido nel VIES?",
        "Coerenza fiscale: TipoDocumento coerente con Natura? Totali coerenti? Numero univoco?",
      ]},
      { type: "p", text:
        "Se uno di questi controlli fallisce, ricevi NS entro pochi minuti dall'invio. La fattura è considerata come mai emessa ai fini fiscali. Devi correggerla e ritrasmetterla." },

      { type: "callout", variant: "coral", title: "Attenzione ai 5 giorni",
        text: "Una NS non protocollata non produce effetti fiscali. Devi correggerla e reinviarla entro 5 giorni per non incorrere in sanzione da omessa fatturazione (art. 6 D.Lgs. 471/97, aggiornato dal D.Lgs. 87/2024)." },

      { type: "h2", id: "codici-errore", text: "I codici errore SDI più frequenti" },
      { type: "table",
        cols: ["Codice", "Errore", "Fix"],
        rows: [
          ["00305", "CF/P.IVA cliente non valido", "Verifica anagrafica del CessionarioCommittente"],
          ["00306", "CF/P.IVA fornitore non valido", "Verifica il VAT ID nel VIES"],
          ["00400", "Natura IVA non ammessa con TipoDocumento", "Correggi la Natura (N6.9 per TD17) o il TD"],
          ["00417", "IdCodice destinatario non valido", "Per soggetti esteri usa sempre 0000000 (sette zeri)"],
          ["00423", "ImponibileImporto o Imposta non congrui", "Ricontrolla arrotondamenti riga per riga"],
          ["00434", "Data documento antecedente inizio P.IVA", "Verifica data di apertura P.IVA nell'anagrafica"],
          ["00437", "Numero fattura duplicato", "Cambia ProgressivoInvio + Numero"],
          ["00443", "TipoDocumento non ammesso in reverse charge", "Verifica coerenza TD-Natura"],
        ]
      },

      { type: "example", subject: "Errore 00417",
        title: "Il caso classico: codice destinatario copiato per errore",
        body: "Marco riemette un'autofattura Google Ads e per abitudine copia il proprio codice destinatario SDI nel campo del fornitore. Google Ireland non ha un codice destinatario italiano — è un soggetto estero. Lo SDI scarta con 00417. Fix: sostituisci con 0000000 (sette zeri) e ritrasmetti.",
        breakdown: [
          { label: "Errore SDI", value: "00417" },
          { label: "Campo problematico", value: "<CodiceDestinatario>" },
          { label: "Valore sbagliato", value: "ABC1234" },
          { label: "Valore corretto", value: "0000000", emphasis: true },
        ],
      },

      { type: "h2", id: "come-correggere", text: "Come correggere e reinviare l'autofattura scartata" },
      { type: "ol", items: [
        "Leggi la notifica NS: nel messaggio c'è il codice errore (00305, 00400, ecc.) e la descrizione testuale",
        "Individua il campo XML incriminato tramite la tabella sopra",
        "Correggi il valore nel gestionale o nell'editor XML",
        "Mantieni stessa numerazione e stessa data della fattura originale (importante!)",
        "Ritrasmetti allo SDI: la nuova versione sostituisce la precedente",
        "Attendi RC (Ricevuta di Consegna): dovresti riceverla entro 15-30 minuti",
      ]},
      { type: "callout", variant: "blue", title: "Come Autofattura by Dasabo aiuta",
        text: "Ogni autofattura viene validata contro il tracciato SDI 1.2.2 prima dell'invio. Se qualcosa non torna, lo mostriamo in dashboard con il fix suggerito. Puoi correggere in 1 click prima di premere \"Invia\", evitando lo scarto in prima battuta." },

      { type: "h2", id: "5-giorni", text: "La regola dei 5 giorni" },
      { type: "p", text:
        "Dopo la NS hai 5 giorni di calendario per ritrasmettere la stessa fattura corretta (stesso numero, stessa data). Se la reinvii entro il termine, ai fini fiscali la data di emissione resta quella originaria e non scattano sanzioni da omessa fatturazione. È una finestra abbastanza generosa ma va rispettata." },

      { type: "h2", id: "sforo-termine", text: "Cosa succede se sforo i 5 giorni" },
      { type: "p", text:
        "Se non ritrasmetti entro 5 giorni, devi emettere un nuovo documento con nuova numerazione e nuova data. La vecchia fattura scartata si considera come mai emessa. Da qui puoi:" },
      { type: "ul", items: [
        "Emettere il documento nuovo e attivare ravvedimento operoso per la sanzione da tardiva registrazione",
        "Sanzione ridotta da 70% a 7,8% se ravvedi entro 90 giorni (1/9)",
        "Consultare il commercialista per casi complessi",
      ]},

      { type: "h2", id: "prevenzione", text: "Come prevenire gli scarti SDI in futuro" },
      { type: "ol", items: [
        "Validazione XML contro tracciato SDI 1.2.2 PRIMA dell'invio (automatica su Autofattura by Dasabo)",
        "Uso di anagrafiche fornitori pre-compilate (Google, Meta, AWS…) con VAT validati VIES",
        "Codice destinatario sempre 0000000 per esteri (mai copiare il proprio)",
        "Controllo automatico totali (Imponibile + Imposta = TotaleDocumento)",
        "Controllo coerenza TD/Natura (TD17 → N6.9, TD18 → N6.9, TD19 → N6.9)",
      ]},

      { type: "quote", text: "Prima ricevevo NS 1 volta su 5, poi ne perdevo 3 giorni a capire cosa avevo sbagliato. Ora la validazione mi dice il problema PRIMA che invii. NS zero da quando uso il servizio.",
        cite: "Michele B., commercialista · Verona" },

      { type: "cta", variant: "outline", label: "Vedi come funziona la validazione pre-invio",
        href: "/docs" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Uno scarto NS non è una catastrofe se lo gestisci nella finestra dei 5 giorni. Ma se accumuli NS ripetuti su ogni invio, il problema è a monte: l'anagrafica fornitori sbagliata, i codici destinatario copiati male, le natura N incoerenti. La soluzione: usa uno strumento che valida prima e blocca gli invii sbagliati alla fonte." },

      { type: "sources", items: [
        { text: "Agenzia delle Entrate — Elenco codici errore SDI (PDF)", href: "https://www.agenziaentrate.gov.it/" },
        { text: "Provvedimento AdE 89757/2018 — regole tecniche fatturazione elettronica" },
        { text: "D.Lgs. 87/2024 — sanzioni per omessa fatturazione" },
        { text: "Art. 6 D.Lgs. 471/97 — sanzioni tributarie amministrative" },
      ]},
    ],
    faq: [
      { q: "Posso reinviare la fattura scartata senza cambiarla?",
        a: "No. Devi correggere l'errore prima. Se reinvii identica, lo SDI la scarta di nuovo con lo stesso codice." },
      { q: "Se la NS arriva dopo il 5° giorno cosa faccio?",
        a: "Emetti nuovo documento con nuova numerazione e nuova data. Segnala al commercialista per l'eventuale ravvedimento operoso." },
      { q: "Ho ricevuto NS ma sono in vacanza, posso delegare?",
        a: "Sì, se hai delegato il commercialista come intermediario SDI può farlo per te. Altrimenti è meglio configurare le notifiche via email/SMS." },
      { q: "Lo scarto NS è comunicato all'AdE come infrazione?",
        a: "No. Lo scarto NS non è una violazione, è un rifiuto tecnico dello SDI. Diventa infrazione solo se non ritrasmetti entro 5 giorni." },
      { q: "Posso vedere lo storico degli NS ricevuti?",
        a: "Sì, sia dal portale Fatture e Corrispettivi AdE che dal tuo intermediario. Su Autofattura by Dasabo c'è una timeline completa di RC/NS/MC per ogni fattura." },
    ],
    relatedSlugs: ["td17-td18-td19-quale-codice-usare", "autofattura-7-errori-comuni", "come-fare-autofattura-google-ads-2026"],
  },

  // ---- 06 · SaaS esteri ------------------------------------------------
  {
    slug: "saas-esteri-autofattura-guida-completa",
    title: "Autofattura per SaaS esteri (Zoom, Canva, Notion, Adobe): guida completa 2026",
    description:
      "Ogni SaaS che paghi con partita IVA richiede autofattura TD17 con reverse charge 22%. Tabella VAT ID reali dei 15 SaaS più usati dai freelance italiani + trattamento extra-UE.",
    publishedAt: "2026-07-11",
    updatedAt: "2026-08-14",
    readingMinutes: 8,
    category: "Guide fiscali",
    keywords: ["autofattura SaaS", "autofattura Zoom", "autofattura Canva", "autofattura Notion", "autofattura Adobe", "TD17 SaaS estero"],
    hero: {
      kicker: "06 · SaaS esteri",
      title: "Autofattura per Zoom, Canva, Notion e gli altri SaaS",
      subtitle:
        "I 15 SaaS più usati dai freelance italiani con VAT ID reali, codici TD e procedura batch per elaborare 15 fatture in 5 minuti.",
    },
    tldr: [
      "Ogni SaaS estero (UE ed extra-UE) richiede autofattura TD17 con reverse charge 22%.",
      "Il freelance italiano medio paga 8-12 SaaS diversi ogni mese: gestione batch quasi obbligatoria.",
      "SaaS extra-UE (Notion, Figma, OpenAI): stesso TD17 ma senza VAT ID intraUE.",
      "Autofattura by Dasabo processa i PDF in batch tramite inbound email dedicato.",
      "Ogni €100/mese di SaaS = ~€22/mese di IVA da versare per il forfettario.",
    ],
    toc: [
      { id: "perche-senza-iva", label: "Perché arrivano senza IVA" },
      { id: "tabella-saas", label: "Tabella VAT dei 15 SaaS più comuni" },
      { id: "extra-ue", label: "SaaS extra-UE: come trattarli" },
      { id: "cosa-fare", label: "Cosa fare per ciascuna fattura" },
      { id: "batch", label: "Elaborazione in batch" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Il freelance italiano medio nel 2026 usa 8-12 SaaS diversi ogni mese: Zoom per le call, Canva per la grafica, Notion per il knowledge, Adobe Creative Cloud per l'editing, Figma per il design, ChatGPT/Claude per l'AI, GitHub per il codice, Slack per il team, Stripe per i pagamenti. Ognuno di questi ti manda una fattura mensile senza IVA italiana. Ognuno richiede una tua autofattura TD17. Se non le fai, ogni €500/mese di SaaS = €1.320/anno di IVA da rischiare in accertamento." },

      { type: "h2", id: "perche-senza-iva", text: "Perché i SaaS esteri fatturano senza IVA italiana" },
      { type: "p", text:
        "Direttiva IVA 2006/112/CE: le prestazioni B2B tra imprese di paesi UE diversi si tassano nel paese del committente (reverse charge). Il fornitore non addebita IVA, l'acquirente la assolve nel proprio paese. Vale anche per fornitori extra-UE con qualche variazione sulla natura giuridica del reverse." },

      { type: "h2", id: "tabella-saas", text: "I 15 SaaS più comuni con VAT ID reali" },
      { type: "table",
        cols: ["SaaS", "Entità legale", "VAT ID", "Paese"],
        rows: [
          ["Zoom", "Zoom Video Communications Ireland", "IE3384253BH", "IE"],
          ["Canva", "Canva Pty Ltd", "—", "AU (extra-UE)"],
          ["Notion", "Notion Labs Inc.", "—", "US (extra-UE)"],
          ["Adobe Creative Cloud", "Adobe Systems Software Ireland", "IE6364992H", "IE"],
          ["Figma", "Figma Inc.", "—", "US (extra-UE)"],
          ["Slack", "Slack Technologies Ireland", "IE9924480R", "IE"],
          ["OpenAI (ChatGPT+, API)", "OpenAI OpCo LLC", "—", "US (extra-UE)"],
          ["Anthropic (Claude)", "Anthropic PBC", "—", "US (extra-UE)"],
          ["GitHub", "GitHub B.V.", "NL855537015B01", "NL"],
          ["Linear", "Linear Orbit Inc.", "—", "US (extra-UE)"],
          ["Stripe (commissioni)", "Stripe Payments Europe Ltd", "IE3206488LH", "IE"],
          ["Shopify", "Shopify International Ltd", "IE3585900MH", "IE"],
          ["Cloudflare", "Cloudflare Portugal Unipessoal", "PT516473846", "PT"],
          ["DigitalOcean", "DigitalOcean LLC", "—", "US (extra-UE)"],
          ["Loom", "Loom Inc.", "—", "US (extra-UE)"],
        ]
      },

      { type: "h2", id: "extra-ue", text: "SaaS extra-UE (Notion, Figma, OpenAI): come trattarli" },
      { type: "p", text:
        "Non tutti i SaaS hanno un'entità EU: Notion, Figma, OpenAI, Anthropic, DigitalOcean, Loom fatturano direttamente dall'entità USA. La procedura è identica (TD17, reverse charge 22%) ma nell'XML:" },
      { type: "ul", items: [
        "IdPaese = US (non IT né codice UE)",
        "IdCodice = codice fiscale/tax ID americano se disponibile (spesso presente in fattura come EIN)",
        "Se il fornitore non ha alcun codice fiscale, ometti IdFiscaleIVA e compila solo Anagrafica + indirizzo",
      ]},

      { type: "example", subject: "Freelance developer",
        title: "Luca usa 8 SaaS diversi",
        body: "Luca è un fullstack developer con 8 SaaS attivi: GitHub (€8), Linear (€10), OpenAI ChatGPT+ (€20), Figma (€15), Notion (€10), Vercel (€20), Slack (gratis), Cloudflare Pro (€20). Totale €103/mese = €1.236/anno di SaaS. IVA reverse charge = €272/anno da versare (se forfettario) o compensare (se ordinario).",
        breakdown: [
          { label: "SaaS attivi", value: "8" },
          { label: "Spesa mensile totale", value: "€ 103,00" },
          { label: "Spesa annua", value: "€ 1.236,00" },
          { label: "IVA reverse charge (22%)", value: "€ 272,00", emphasis: true },
        ],
      },

      { type: "h2", id: "cosa-fare", text: "Cosa fare per ciascuna fattura SaaS" },
      { type: "ol", items: [
        "Scarica il PDF dalla dashboard del SaaS (di solito Billing > Invoices)",
        "Verifica dati fornitore (entità legale, VAT ID se UE)",
        "Crea autofattura TD17 con anagrafica corretta",
        "Applica reverse charge 22% con Natura N6.9",
        "Trasmetti allo SDI direttamente o via Fatture in Cloud",
      ]},

      { type: "h2", id: "batch", text: "Elaborazione in batch: 15 SaaS in 5 minuti" },
      { type: "p", text:
        "Con 8-15 SaaS diversi ogni mese, il flusso manuale (una fattura alla volta) diventa insostenibile. Best practice:" },
      { type: "ul", items: [
        "Configura un'unica cartella email per tutte le ricevute SaaS",
        "Attiva regola Gmail: mittente = billing@* OR receipts@* → inoltra a Autofattura by Dasabo",
        "Il sistema estrae tutti i PDF, li classifica, li mostra in dashboard con TD17 pre-impostato",
        "Verifichi le bozze in blocco, confermi in 1 click e trasmetti tutte allo SDI",
      ]},

      { type: "quote", text: "12 SaaS al mese, 12 fatture da integrare. Prima erano 2 ore di lavoro mensile. Adesso sono 4 minuti: apro la dashboard, controllo, invio tutto in blocco.",
        cite: "Giulia R., product designer freelance · Milano" },

      { type: "cta", variant: "dark", label: "Prova gratis batch mode con 5 crediti",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Un singolo SaaS da €10/mese sembra insignificante da un punto di vista fiscale. Ma se ne usi 10, sono €120/mese di reverse charge da integrare. In un anno €1.440 di IVA. Al minimo €250 di sanzione per fattura non integrata = €30.000/anno di potenziale rischio se lasci correre. La disciplina è: batch mensile, tutto insieme, automatizzato." },

      { type: "sources", items: [
        { text: "VIES per verifica VAT ID EU", href: "https://ec.europa.eu/taxation_customs/vies/" },
        { text: "Direttiva IVA 2006/112/CE — reverse charge B2B UE" },
        { text: "Risoluzione AdE 89/E 2020 — servizi digitali extra-UE" },
      ]},
    ],
    faq: [
      { q: "Il SaaS extra-UE senza VAT ID (Notion, Figma, OpenAI) come lo integro?",
        a: "Codice TD17 comunque. Nel CedentePrestatore inserisci denominazione + indirizzo estero. IdCodice del VAT: se il fornitore fornisce un EIN/Tax ID USA usalo, altrimenti ometti IdFiscaleIVA e compila solo Anagrafica." },
      { q: "Se ho abbonamento annuale posso integrarlo una volta all'anno?",
        a: "Sì. Il fornitore emette 1 fattura all'anno per l'abbonamento annuale, quindi tu fai 1 autofattura all'anno." },
      { q: "I refund SaaS generano nota di credito TD04?",
        a: "Sì. Se il SaaS ti rimborsa (o annulla parzialmente) devi emettere TD04 di segno negativo con riferimento all'autofattura originale." },
      { q: "Devo integrare anche le sottoscrizioni free trial?",
        a: "No, solo se hai pagato effettivamente. Il free trial non genera fattura, quindi non genera obbligo di autofattura." },
    ],
    relatedSlugs: ["come-fare-autofattura-google-ads-2026", "td17-td18-td19-quale-codice-usare", "autofattura-stripe-2026"],
  },

  // ---- 07 · TikTok Ads --------------------------------------------------
  {
    slug: "autofattura-tiktok-ads",
    title: "Autofattura TikTok Ads 2026: guida per creator e brand italiani",
    description:
      "TikTok Advertising Ireland fattura da Dublino. Come integrare la spesa TikTok Ads con TD17, reverse charge 22% e VAT IE3760099QH — anche per creator forfettari.",
    publishedAt: "2026-07-18",
    updatedAt: "2026-08-14",
    readingMinutes: 6,
    category: "Guide fiscali",
    keywords: ["autofattura TikTok Ads", "TikTok Advertising Ireland VAT", "TD17 TikTok", "creator forfettario TikTok"],
    hero: {
      kicker: "07 · TikTok Ads",
      title: "Autofattura TikTok Ads: pubblicità dal cuore di Dublino",
      subtitle:
        "Se paghi TikTok Ads con partita IVA italiana la fattura arriva da TikTok Advertising Ireland Ltd. Guida operativa a TD17, reverse charge 22% e trattamento per creator forfettari.",
    },
    tldr: [
      "TikTok Advertising Ireland Ltd (VAT IE3760099QH) fattura tutte le operazioni EU dal 2022.",
      "Codice TD17, reverse charge 22%, natura N6.9.",
      "Anche per creator forfettari: IVA indeducibile, ogni €100 di TikTok Ads costa €122.",
      "Il resoconto TikTok Ads Manager NON è la fattura fiscale: va scaricato il PDF dal billing.",
    ],
    toc: [
      { id: "chi-fattura", label: "Chi fattura davvero" },
      { id: "dati-tiktok", label: "Dati fornitore TikTok" },
      { id: "creator", label: "TikTok anche per creator" },
      { id: "esempi-costo", label: "Esempi di costo tipici" },
      { id: "automatizza", label: "Come automatizzarla" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "TikTok non è più solo dance e trend virali: nel 2026 è la seconda piattaforma pubblicitaria più utilizzata dai creator e brand italiani sotto i 35 anni, secondo IAB Italia. E come ogni pubblicità digitale acquistata da soggetto estero, richiede un'autofattura elettronica TD17 con reverse charge 22%." },

      { type: "h2", id: "chi-fattura", text: "Chi ti fattura TikTok Ads" },
      { type: "p", text:
        "Dal 2022 tutti gli inserzionisti B2B della UE ricevono fatture da TikTok Advertising Ireland Limited, con sede a Dublino. Prima era TikTok Information Technologies UK Limited, ma dopo Brexit le operazioni EU sono state consolidate sotto l'entità irlandese." },
      { type: "callout", variant: "coral", title: "Non basta il PDF interno",
        text: "TikTok Ads Manager mostra un \"riepilogo spesa\" che NON è una fattura fiscale. La fattura ufficiale con VAT ID va scaricata dalla sezione \"Fatture\" del billing. Solo quella conta per l'autofattura." },

      { type: "h2", id: "dati-tiktok", text: "I dati fornitore corretti di TikTok" },
      { type: "table", cols: ["Campo", "Valore"], rows: [
        ["Denominazione", "TikTok Advertising Ireland Ltd"],
        ["VAT ID", "IE3760099QH"],
        ["Indirizzo", "The Sorting Office, Ropemaker Place, Dublin 2, Ireland"],
        ["Paese", "IE (Irlanda)"],
        ["Codice destinatario SDI", "0000000"],
      ]},

      { type: "h2", id: "creator", text: "TD17 vale anche per creator e influencer" },
      { type: "p", text:
        "Se sei un creator con partita IVA (RF19 forfettario o RF01 ordinario) e sponsorizzi post su TikTok, l'obbligo è identico a quello di una PMI: autofattura TD17, natura N6.9, reverse charge 22%. Non esiste un regime agevolato per i creator." },

      { type: "example", subject: "Creator forfettario",
        title: "Sofia, creator lifestyle · €300/mese TikTok Ads",
        body: "Sofia sponsorizza i suoi contenuti TikTok per €300/mese. In forfettario, i €66 di IVA reverse charge sono indeducibili. Ogni €100 di adv le costano €122. Sofia versa €792/anno in F24 (codici 6003/6006/6009/6012) trimestralmente.",
        breakdown: [
          { label: "Spesa mensile TikTok Ads", value: "€ 300,00" },
          { label: "IVA reverse charge 22%", value: "€ 66,00" },
          { label: "Versamento annuo F24", value: "€ 792,00", emphasis: true },
          { label: "Rischio se non integra (70%)", value: "€ 554 + €250 min", emphasis: true },
        ],
      },

      { type: "h2", id: "esempi-costo", text: "Esempi di costo tipici" },
      { type: "table",
        cols: ["Profilo", "Spesa mensile TikTok", "IVA da versare/anno"],
        rows: [
          ["Creator forfettario", "€ 200", "€ 528"],
          ["Piccola azienda ordinaria", "€ 800", "€ 0 (saldo zero)"],
          ["Agenzia media", "€ 5.000", "€ 0 (saldo zero)"],
        ]
      },

      { type: "h2", id: "automatizza", text: "Come automatizzarla in 30 secondi" },
      { type: "p", text:
        "Trascini il PDF TikTok su Autofattura by Dasabo (o inoltri l'email che TikTok ti manda ogni volta che addebita una carta). L'AI legge fornitore, VAT irlandese, imponibile e valuta. Genera l'XML TD17 con reverse charge 22% e lo trasmette allo SDI direttamente." },
      { type: "cta", variant: "dark", label: "Prova con 5 crediti gratuiti (senza carta)",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "TikTok Ads è dal 2022 pienamente allineato agli altri media (Meta, Google): fatturazione da Dublino, VAT irlandese, TD17 in reverse charge. Nessuna differenza fiscale. Cambia solo la piattaforma pubblicitaria. Per creator e agenzie il flusso è meccanico e altamente automatizzabile." },

      { type: "sources", items: [
        { text: "TikTok Business Help Center · Billing & Payments", href: "https://ads.tiktok.com/help/" },
        { text: "VIES per VAT IE3760099QH", href: "https://ec.europa.eu/taxation_customs/vies/" },
        { text: "DPR 633/72 art. 17 c. 2" },
      ]},
    ],
    faq: [
      { q: "TikTok mi fatturava dall'UK, posso ancora usare le vecchie fatture?",
        a: "Se hai fatture con VAT UK (GB) di TikTok Information Technologies UK Limited pre-2022, restano valide fiscalmente. Il codice TD è sempre TD17." },
      { q: "TikTok Shop segue lo stesso trattamento?",
        a: "Sì. Se vendi su TikTok Shop, la P.IVA riceve un servizio marketplace da TikTok — stesso schema: TD17 in reverse charge sulla commissione." },
      { q: "Serve dichiarare separatamente TikTok e Meta se sponsorizzo entrambi?",
        a: "Sì. Ogni fornitore = una autofattura distinta. Non si accorpano." },
    ],
    relatedSlugs: ["autofattura-meta-ads-facebook-instagram", "come-fare-autofattura-google-ads-2026", "regime-forfettario-autofatture-estere"],
  },

  // ---- 08 · LinkedIn Ads -----------------------------------------------
  {
    slug: "autofattura-linkedin-ads",
    title: "Autofattura LinkedIn Ads: come integrare le fatture di LinkedIn Ireland 2026",
    description:
      "LinkedIn Ireland Unlimited Company fattura Sponsored Content, InMail e Text Ads. Guida a TD17, reverse charge 22%, VAT IE9740425P e come evitare lo scarto NS 00417.",
    publishedAt: "2026-07-24",
    updatedAt: "2026-08-14",
    readingMinutes: 6,
    category: "Guide fiscali",
    keywords: ["autofattura LinkedIn Ads", "LinkedIn Ireland VAT", "TD17 LinkedIn", "sponsored content autofattura"],
    hero: {
      kicker: "08 · LinkedIn Ads",
      title: "Autofattura LinkedIn Ads: dalla Sponsored Content al TD17",
      subtitle:
        "LinkedIn Ireland Unlimited Company fattura tutta la pubblicità europea. Guida completa a TD17, scarto NS 00417 più frequente e budget alti da agenzia.",
    },
    tldr: [
      "LinkedIn Ireland Unlimited Company (VAT IE9740425P) fattura tutta la pubblicità EU.",
      "Prodotti coperti: Sponsored Content, InMail, Text Ads, Dynamic Ads, Lead Gen Forms, Sales Navigator.",
      "Codice TD17, Natura N6.9, reverse charge 22% — come tutti gli altri media.",
      "Lo scarto SDI 00417 è il più frequente su LinkedIn: dimenticare 0000000 nel codice destinatario.",
    ],
    toc: [
      { id: "chi-fattura", label: "Chi fattura davvero" },
      { id: "dati-linkedin", label: "Dati fornitore LinkedIn" },
      { id: "prodotti", label: "Prodotti coperti" },
      { id: "ns-00417", label: "Il codice NS 00417 più comune" },
      { id: "budget-alti", label: "Budget alti e agenzie" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "LinkedIn è il B2B per eccellenza: il CPM è alto (€40-60) ma il lead-quality è altrettanto. Le agenzie che gestiscono clienti enterprise su LinkedIn spendono facilmente €5.000-20.000/mese in Sponsored Content e Message Ads. Ogni euro va integrato con autofattura TD17 dallo studio di Wilton Plaza, Dublino." },

      { type: "h2", id: "chi-fattura", text: "Chi ti fattura LinkedIn Ads" },
      { type: "p", text:
        "Ogni volta che sponsorizzi un post, mandi una InMail massiva o attivi Text Ads, la fattura arriva da LinkedIn Ireland Unlimited Company. È l'entità EU di LinkedIn (di proprietà Microsoft) che gestisce tutta la fatturazione B2B europea. Zero IVA irlandese in fattura — sei tu il debitore d'imposta in reverse charge." },

      { type: "h2", id: "dati-linkedin", text: "Dati fornitore corretti" },
      { type: "table", cols: ["Campo", "Valore"], rows: [
        ["Denominazione", "LinkedIn Ireland Unlimited Company"],
        ["VAT ID", "IE9740425P"],
        ["Indirizzo", "Wilton Plaza, Wilton Place, Dublin 2, Ireland"],
        ["Paese", "IE (Irlanda)"],
        ["Codice destinatario SDI", "0000000"],
      ]},

      { type: "h2", id: "prodotti", text: "Tutti i prodotti LinkedIn Ads coperti" },
      { type: "ul", items: [
        "Sponsored Content (post promossi nel feed)",
        "Sponsored InMail / Message Ads (messaggi diretti)",
        "Text Ads (banner laterali desktop)",
        "Dynamic Ads (banner personalizzati)",
        "Video Ads (nel feed)",
        "Lead Gen Forms (form nativi LinkedIn)",
        "Sales Navigator subscription",
        "LinkedIn Learning per team",
      ]},
      { type: "p", text:
        "Sono tutti servizi pubblicitari elettronici e SaaS B2B, quindi rientrano nel medesimo codice TD17 con reverse charge 22%. Non serve un codice diverso per prodotto." },

      { type: "h2", id: "ns-00417", text: "Lo scarto SDI più comune con LinkedIn: 00417" },
      { type: "p", text:
        "Se lo SDI ti restituisce NS con codice 00417 (\"IdCodice non valido\") su un'autofattura LinkedIn, quasi sempre il problema è nel <CessionarioCommittente>: hai inserito un IdCodice sbagliato per il codice destinatario. Per soggetti esteri il codice destinatario deve essere sempre 0000000 (sette zeri). Il VAT ID va nel campo <IdFiscaleIVA>." },
      { type: "callout", variant: "blue", title: "Fix con Autofattura by Dasabo",
        text: "Non serve ricordarlo: la piattaforma imposta automaticamente 0000000 per tutti i fornitori esteri, evitando questa classe di NS." },

      { type: "example", subject: "Agenzia B2B",
        title: "Roma Marketing Srl · €10.000/mese LinkedIn Ads",
        body: "Roma Marketing gestisce campagne LinkedIn per 3 clienti enterprise. Ogni mese ricevono 1 fattura da LinkedIn Ireland da €10.000. Integrano TD17, reverse charge €2.200. Con Autofattura by Dasabo in modalità multi-azienda, l'agenzia può aggregare i costi sotto ciascun cliente.",
        breakdown: [
          { label: "Spesa LinkedIn mensile aggregata", value: "€ 10.000,00" },
          { label: "IVA reverse charge 22%", value: "€ 2.200,00" },
          { label: "IVA annua", value: "€ 26.400,00", emphasis: true },
        ],
      },

      { type: "h2", id: "budget-alti", text: "Budget alti: agenzie e enterprise" },
      { type: "p", text:
        "Con budget LinkedIn oltre €10k/mese l'IVA reverse charge diventa rilevante (>€2.200). Consigli pratici:" },
      { type: "ol", items: [
        "Consolida più account pubblicitari sotto un solo Business Manager per ricevere 1 sola fattura mensile",
        "Attiva inbound email dedicata per raccogliere le fatture LinkedIn automaticamente",
        "Se sei agenzia con più clienti, usa la modalità multi-azienda per aggregare gli account clienti sotto lo studio",
      ]},
      { type: "cta", variant: "dark", label: "Automatizza le fatture LinkedIn — 5 crediti gratis",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "LinkedIn Ads è meccanico come Meta: stessa entità legale, stesso codice, stessa procedura. La sola differenza è che l'importo unitario tende a essere più alto (LinkedIn è più costoso al CPM). Per le agenzie B2B che gestiscono più clienti, il flusso multi-azienda è la scelta operativa più efficiente." },

      { type: "sources", items: [
        { text: "LinkedIn Marketing Solutions Help Center", href: "https://business.linkedin.com/marketing-solutions" },
        { text: "VIES per VAT IE9740425P", href: "https://ec.europa.eu/taxation_customs/vies/" },
      ]},
    ],
    faq: [
      { q: "Sales Navigator la integro allo stesso modo?",
        a: "Sì. Sales Navigator è fatturato da LinkedIn Ireland con lo stesso VAT IE9740425P. TD17 e reverse charge 22%." },
      { q: "Se ho crediti pubblicitari LinkedIn che paga il mio cliente, chi fa l'autofattura?",
        a: "Chi ha la P.IVA sull'account di fatturazione LinkedIn. Se il cliente ti riaddebita il costo, tu fatturi il servizio pubblicitario al cliente separatamente." },
      { q: "LinkedIn Learning per team lo integro?",
        a: "Sì, stesso VAT + TD17 + reverse charge 22%." },
    ],
    relatedSlugs: ["autofattura-meta-ads-facebook-instagram", "come-fare-autofattura-google-ads-2026", "sdi-scarta-autofattura-codice-ns"],
  },

  // ---- 09 · Cloudflare -------------------------------------------------
  {
    slug: "autofattura-cloudflare",
    title: "Autofattura Cloudflare 2026: Workers, R2, Pages, DNS e piani Pro/Business",
    description:
      "Cloudflare fattura dalla sua sede portoghese (VAT PT516473846). Guida completa a Workers, R2, Pages, DNS e piani Pro/Business con TD17 e reverse charge 22%.",
    publishedAt: "2026-07-30",
    updatedAt: "2026-08-14",
    readingMinutes: 6,
    category: "Guide fiscali",
    keywords: ["autofattura Cloudflare", "Cloudflare Portugal VAT", "TD17 Cloudflare", "reverse charge Workers", "R2 Pages autofattura"],
    hero: {
      kicker: "09 · Cloudflare",
      title: "Autofattura Cloudflare: dal DNS a Workers, tutto in un TD17",
      subtitle:
        "Cloudflare Portugal Unipessoal Lda emette per tutta l'Europa. Guida operativa a tutti i prodotti — dai piani Pro a Workers, R2, Pages e servizi enterprise — con TD17 e reverse charge 22%.",
    },
    tldr: [
      "Cloudflare Portugal Unipessoal Lda (VAT PT516473846) fattura tutta la clientela EU dal 2020.",
      "Codice TD17 per tutti i prodotti: piani Pro/Business, Workers, R2, Pages, Zero Trust, Registrar.",
      "Fatture usage-based (R2, Workers): possono variare drasticamente mese su mese.",
      "Anche micro-fatture da €0,80 vanno integrate: l'AdE controlla i pagamenti aggregati per VAT ID.",
    ],
    toc: [
      { id: "perche-portogallo", label: "Perché arriva dal Portogallo" },
      { id: "dati-cloudflare", label: "Dati fornitore" },
      { id: "prodotti", label: "Tutti i prodotti Cloudflare" },
      { id: "usage-based", label: "Fatture usage-based (R2, Workers)" },
      { id: "automazione", label: "Automazione con inbound" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Cloudflare è la spina dorsale dell'internet moderno: DNS, CDN, WAF, edge computing con Workers, object storage con R2, hosting statico con Pages. Se sei uno sviluppatore, agenzia digitale, e-commerce o SaaS, probabilmente hai Cloudflare in produzione. E ogni mese ricevi una fattura da Lisbona con VAT portoghese." },

      { type: "h2", id: "perche-portogallo", text: "Perché la fattura arriva dal Portogallo" },
      { type: "p", text:
        "Cloudflare ha stabilito la sua sede fiscale EU a Lisbona nel 2020, e da allora tutti i clienti business europei ricevono fatture da Cloudflare Portugal Unipessoal Lda. Prima era Cloudflare Inc. (USA), quindi se hai vecchie fatture con VAT US si trattava di TD17 comunque, ma senza VAT ID intraUE." },
      { type: "callout", variant: "blue", title: "Nota tecnica",
        text: "Cloudflare Portugal Unipessoal Lda è una società unipersonale portoghese (\"Unipessoal\" = LLC). La sua VAT PT516473846 è validata VIES ed è quella corretta da usare nell'autofattura." },

      { type: "h2", id: "dati-cloudflare", text: "Dati fornitore corretti" },
      { type: "table", cols: ["Campo", "Valore"], rows: [
        ["Denominazione", "Cloudflare Portugal Unipessoal Lda"],
        ["VAT ID", "PT516473846"],
        ["Indirizzo", "Largo Rafael Bordalo Pinheiro 29, 1200-369 Lisboa"],
        ["Paese", "PT (Portogallo)"],
        ["Codice destinatario SDI", "0000000"],
      ]},

      { type: "h2", id: "prodotti", text: "Tutti i prodotti Cloudflare che fatturano" },
      { type: "table",
        cols: ["Prodotto", "Modello di prezzo", "Frequenza"],
        rows: [
          ["Piani Pro / Business / Enterprise", "Abbonamento mensile fisso", "Mensile"],
          ["Workers / Workers KV", "Usage-based (richieste + CPU)", "Mensile"],
          ["R2 Object Storage", "Usage-based (GB + operazioni)", "Mensile"],
          ["Pages", "Free tier + overage", "Mensile se overage"],
          ["Zero Trust / Access", "Per-seat mensile", "Mensile"],
          ["Zaraz", "Free tier + Pro", "Mensile"],
          ["Argo Smart Routing", "Add-on variabile", "Mensile"],
          ["Registrar", "Costo dominio + fee", "Annuale/rinnovo"],
        ]
      },

      { type: "h2", id: "usage-based", text: "Attenzione alle fatture usage-based (R2, Workers)" },
      { type: "p", text:
        "Se usi R2 come backend storage o Workers per API serverless, la fattura mensile può variare da pochi centesimi a centinaia di euro senza preavviso in base al traffico. Best practice fiscale:" },
      { type: "ol", items: [
        "Imposta email alert Cloudflare su costo mensile > soglia (Billing → Notifications)",
        "Attiva inbound email della piattaforma di autofattura per non perdere fatture di importo basso",
        "Verifica mensilmente l'estratto conto: fatture <€1 vanno comunque integrate",
      ]},
      { type: "callout", variant: "amber", title: "Micro-fatture non trascurabili",
        text: "Una fattura Cloudflare da €0,80 va comunque integrata: l'AdE controlla i pagamenti aggregati per VAT ID nel VIES. Meglio 12 micro-autofatture in ordine che una lacuna anche piccola." },

      { type: "h2", id: "automazione", text: "Automazione con inbound email Cloudflare" },
      { type: "p", text:
        "Le fatture Cloudflare arrivano puntuali ogni 1° del mese dall'indirizzo billing@cloudflare.com. Configura una regola Gmail per inoltrarle automaticamente al tuo indirizzo Autofattura by Dasabo. Le fatture entrano in dashboard senza tocco manuale, pronte per la trasmissione batch a fine mese." },
      { type: "cta", variant: "dark", label: "Setup inbound email in 2 minuti — prova gratis",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Cloudflare è un tipico caso in cui l'automazione via inbound email si giustifica anche per micro-importi. Il rischio principale non è l'importo assoluto ma la lacuna nel VIES: meglio 12 autofatture da €5 che 11 e una mancante. Il flusso Autofattura by Dasabo lo rende un problema risolto." },

      { type: "sources", items: [
        { text: "Cloudflare Billing Documentation", href: "https://developers.cloudflare.com/billing/" },
        { text: "VIES per VAT PT516473846", href: "https://ec.europa.eu/taxation_customs/vies/" },
      ]},
    ],
    faq: [
      { q: "Ho ancora fatture vecchie da Cloudflare Inc. (USA), come le integro?",
        a: "Sempre TD17 (servizio estero), ma nel CedentePrestatore inserisci Cloudflare Inc. e IdPaese US senza VAT ID intraUE. Il reverse charge 22% resta uguale." },
      { q: "R2 storage rientra come bene o servizio?",
        a: "Servizio digitale (storage-as-a-service). Codice TD17, non TD18. TD18 vale solo per beni fisici." },
      { q: "Cloudflare Registrar (registrazione domini) segue lo stesso trattamento?",
        a: "Sì. Anche i domini registrati via Cloudflare sono fatturati da Cloudflare Portugal, TD17, reverse charge 22%." },
    ],
    relatedSlugs: ["saas-esteri-autofattura-guida-completa", "autofattura-stripe-2026", "come-fare-autofattura-google-ads-2026"],
  },

  // ---- 10 · PayPal ----------------------------------------------------
  {
    slug: "autofattura-paypal-2026",
    title: "Autofattura PayPal 2026: guida a TD17 per commissioni e pagamenti",
    description:
      "PayPal addebita commissioni dal Lussemburgo (VAT LU22046007). Come integrare le autofatture TD17 mensili con reverse charge 22% — anche se sono €2 al mese.",
    publishedAt: "2026-08-04",
    updatedAt: "2026-08-14",
    readingMinutes: 7,
    category: "Guide fiscali",
    keywords: ["autofattura PayPal", "TD17 PayPal", "commissioni PayPal", "PayPal Luxembourg VAT", "reverse charge PayPal"],
    hero: {
      kicker: "10 · PayPal",
      title: "Autofattura PayPal: le commissioni che ti dimentichi di integrare",
      subtitle:
        "PayPal addebita ogni mese piccole commissioni dal Lussemburgo. Serve la loro autofattura TD17 — anche se sono €2,60 al mese. Guida completa 2026 con ravvedimento.",
    },
    tldr: [
      "PayPal (Europe) S.à r.l. et Cie SCA (VAT LU22046007) fattura tutte le commissioni EU dal Lussemburgo.",
      "Ogni transazione ricevuta ha una commissione (2,9% + fee fissa €0,35): tutte da integrare mensilmente.",
      "TD17, Natura N6.9, reverse charge 22% — anche per micro-fatture da €2/mese.",
      "Forfettari: €10.000/mese di transazioni PayPal = ~€800/anno di IVA da versare F24.",
      "Ravvedimento operoso possibile per anni pregressi non integrati.",
    ],
    toc: [
      { id: "perche-commissioni", label: "Perché PayPal ti addebita senza IVA" },
      { id: "dati-paypal", label: "Dati fornitore corretti" },
      { id: "td17-piccole", label: "TD17 anche per €2 al mese" },
      { id: "forfettari", label: "Forfettari: quanto costa" },
      { id: "ravvedimento", label: "Ravvedimento per anni pregressi" },
      { id: "automazione", label: "Come automatizzarla" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Se hai un e-commerce, se ricevi pagamenti da clienti internazionali, se usi PayPal come metodo di incasso alternativo alla carta di credito diretta, ogni mese PayPal trattiene commissioni sul tuo denaro. Ogni transazione paga 2,9% + fee fissa €0,35. In un mese medio di 300 transazioni da €25 = commissioni per €322. Sono €70/mese di IVA reverse charge da integrare, oppure sanzione se dimentichi." },

      { type: "h2", id: "perche-commissioni", text: "Perché PayPal ti fattura senza IVA italiana" },
      { type: "p", text:
        "PayPal opera in Europa tramite PayPal (Europe) S.à r.l. et Cie SCA, con sede in Lussemburgo. Ogni volta che ricevi un pagamento tramite PayPal, la piattaforma trattiene una commissione. Questa commissione è una prestazione di servizi B2B tra soggetto UE non italiano e la tua Partita IVA — quindi arriva senza IVA italiana e sei tu il debitore d'imposta in reverse charge (art. 17 c. 2 DPR 633/72)." },
      { type: "callout", variant: "coral", title: "L'errore più comune",
        text: "Migliaia di freelance e PMI italiane usano PayPal da anni senza integrare mai le commissioni. Basta un incrocio VIES tra AdE e PayPal Luxembourg per far scattare un accertamento. La sanzione D.Lgs. 87/2024 è del 70% dell'IVA non versata, con minimo €250 a fattura." },

      { type: "h2", id: "dati-paypal", text: "Dati fornitore corretti" },
      { type: "table", cols: ["Campo", "Valore"], rows: [
        ["Denominazione", "PayPal (Europe) S.à r.l. et Cie SCA"],
        ["VAT ID", "LU22046007"],
        ["Indirizzo", "22-24 Boulevard Royal, L-2449 Luxembourg"],
        ["Paese", "LU (Lussemburgo)"],
        ["Codice destinatario SDI", "0000000"],
      ]},

      { type: "h2", id: "td17-piccole", text: "TD17 anche per commissioni da €2" },
      { type: "p", text:
        "Non esiste una soglia minima. Anche una commissione mensile da €2,60 va integrata con autofattura elettronica TD17. Per non impazzire con una micro-fattura al mese, la prassi corretta è aggregare tutte le commissioni del mese in un'unica autofattura mensile, con data 1° del mese successivo." },
      { type: "ol", items: [
        "Scarica il resoconto mensile da PayPal Business (Reports → Statements)",
        "Estrai il totale commissioni del mese",
        "Emetti 1 autofattura TD17 aggregata mensile",
        "Applica Natura N6.9 e reverse charge 22%",
      ]},

      { type: "h2", id: "forfettari", text: "Forfettari: la commissione ti costa il 22% in più" },
      { type: "example", subject: "E-commerce forfettario",
        title: "Giovanni, e-commerce accessori · €10k/mese transazioni PayPal",
        body: "Giovanni gestisce uno store Shopify. Riceve €10.000/mese di transazioni PayPal. Le commissioni PayPal sono ~€320/mese (2,9% + €0,35 × 350 transazioni). €70/mese di IVA reverse charge = €840/anno da versare in F24 (indeducibile). Se non integra: sanzione ~€1.400 + €250×12 = €4.400 di rischio.",
        breakdown: [
          { label: "Transazioni PayPal mensili", value: "€ 10.000" },
          { label: "Commissioni PayPal (media 3,2%)", value: "€ 320" },
          { label: "IVA reverse charge annua", value: "€ 845", emphasis: true },
          { label: "Rischio se non integra (70% + min)", value: "~€ 3.400-4.400" },
        ],
      },

      { type: "callout", variant: "amber", title: "Alternativa",
        text: "Se il volume PayPal è alto, valuta con il commercialista se conviene passare a Stripe (commissioni simili ma fatturazione EU allineata) o gestori PSP italiani (fatture con IVA italiana normale, niente autofattura da fare)." },

      { type: "h2", id: "ravvedimento", text: "Ravvedimento operoso per anni pregressi" },
      { type: "p", text:
        "Se non hai mai integrato le commissioni PayPal (situazione comune), puoi metterti in regola con ravvedimento operoso (art. 13 D.Lgs. 472/97). Attivi il ravvedimento emettendo le autofatture mancanti con date retroattive e pagando sanzione ridotta:" },
      { type: "table",
        cols: ["Tempo dal ritardo", "Riduzione sanzione", "Sanzione effettiva"],
        rows: [
          ["≤ 90 giorni", "1/9", "7,8%"],
          ["≤ 1 anno", "1/8", "8,8%"],
          ["≤ 2 anni", "1/7", "10%"],
          ["> 2 anni", "1/6", "11,7%"],
        ]
      },

      { type: "quote", text: "Avevo 24 mesi di commissioni PayPal non integrate. Con Autofattura by Dasabo le ho generate tutte in batch e ho fatto ravvedimento con €340 di sanzioni invece dei €4.200 che avrei rischiato in accertamento.",
        cite: "Marta L., e-commerce fashion · Milano" },

      { type: "h2", id: "automazione", text: "Come automatizzarla in 60 secondi" },
      { type: "p", text:
        "Su Autofattura by Dasabo puoi caricare il resoconto mensile PayPal (PDF o CSV). L'AI estrae il totale commissioni e la data. Poi genera la TD17 aggregata con reverse charge preimpostato per la tua P.IVA e la trasmette allo SDI." },
      { type: "cta", variant: "dark", label: "Prova gratis con 5 crediti (senza carta)",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Le commissioni PayPal sono il classico costo \"nascosto\" che sfugge alla contabilità perché non lo paghi con un F24 o un bonifico ma viene decurtato direttamente dai tuoi incassi. Il risultato: molti freelance e PMI hanno anni di lacune nell'integrazione IVA senza saperlo. Il rimedio è meccanico: aggregazione mensile automatizzata + ravvedimento retroattivo se necessario. La disciplina di 5 minuti al mese ti mette al riparo da €1.000-5.000/anno di rischio." },

      { type: "sources", items: [
        { text: "PayPal Business · Statements", href: "https://www.paypal.com/businessmanage/" },
        { text: "VIES per LU22046007", href: "https://ec.europa.eu/taxation_customs/vies/" },
        { text: "Art. 13 D.Lgs. 472/97 — ravvedimento operoso" },
      ]},
    ],
    faq: [
      { q: "Devo integrare anche le commissioni sui pagamenti B2C (verso consumatori privati)?",
        a: "Sì. La commissione PayPal è un servizio verso di te, indipendentemente da chi paga. Vale sempre TD17 in reverse charge." },
      { q: "PayPal ha un'entità irlandese come Meta e Google?",
        a: "No. Per l'Europa PayPal opera esclusivamente da Lussemburgo con VAT LU22046007." },
      { q: "Se non ho mai fatto le autofatture PayPal come metto in regola gli anni passati?",
        a: "Ravvedimento operoso: emetti autofatture retroattive con le date corrette e paghi sanzioni ridotte (dal 7,8% al 11,7%)." },
      { q: "PayPal Zettle (POS) segue lo stesso trattamento?",
        a: "Sì, stesso VAT LU22046007, stesso TD17 reverse charge 22%." },
    ],
    relatedSlugs: ["autofattura-stripe-2026", "regime-forfettario-autofatture-estere", "autofattura-7-errori-comuni"],
  },

  // ---- 11 · Stripe -----------------------------------------------------
  {
    slug: "autofattura-stripe-2026",
    title: "Autofattura Stripe 2026: guida a reverse charge e TD17 dopo il cambio del 2025",
    description:
      "Da luglio 2025 Stripe fattura da Stripe Payments Europe Ltd (VAT IE3206488LH). Cosa cambia rispetto a Stripe Inc. USA, TD17 e trattamento per forfettari.",
    publishedAt: "2026-08-07",
    updatedAt: "2026-08-14",
    readingMinutes: 7,
    category: "Guide fiscali",
    keywords: ["autofattura Stripe", "Stripe Ireland VAT", "commissioni Stripe", "TD17 Stripe", "Stripe Payments Europe"],
    hero: {
      kicker: "11 · Stripe",
      title: "Autofattura Stripe: cambio di rotta dal luglio 2025",
      subtitle:
        "Fino al 2024 Stripe fatturava dagli USA. Dal luglio 2025 tutte le commissioni EU passano da Stripe Payments Europe (Irlanda, VAT IE3206488LH). Guida operativa completa a TD17.",
    },
    tldr: [
      "Dal 1° luglio 2025 Stripe fattura da Stripe Payments Europe Ltd (VAT IE3206488LH), non più da Stripe Inc. USA.",
      "TD17, Natura N6.9, reverse charge 22% — nessuna differenza fiscale con la vecchia versione USA.",
      "Vecchie fatture Stripe Inc. restano valide con TD17 senza VAT intraUE.",
      "Le commissioni possono variare drasticamente in base al volume: automazione via inbound email raccomandata.",
    ],
    toc: [
      { id: "cambio-2025", label: "Il cambio di luglio 2025" },
      { id: "dati-stripe", label: "Dati fornitore aggiornati" },
      { id: "td17-commissioni", label: "TD17 sulle commissioni" },
      { id: "vecchie-fatture", label: "Vecchie fatture Stripe Inc. USA" },
      { id: "casi-particolari", label: "Casi particolari" },
      { id: "automazione", label: "Automazione" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Se accetti pagamenti online sul tuo sito o app, è molto probabile che tu usi Stripe: la sua quota di mercato tra le PMI/startup europee è cresciuta al 43% nel 2025. E come ogni PSP internazionale, Stripe ti addebita commissioni che tu devi integrare con autofattura TD17. La novità del 2025: le fatture non arrivano più dagli USA ma da Dublino." },

      { type: "h2", id: "cambio-2025", text: "Il cambio del 1° luglio 2025" },
      { type: "p", text:
        "Fino al 30 giugno 2025 le commissioni Stripe erano fatturate da Stripe Inc. (Delaware, USA) senza VAT ID EU. Le autofatture erano TD17 su fornitore extra-UE, con indirizzo americano nel <CedentePrestatore>. Dal 1° luglio 2025 tutte le operazioni EU sono migrate su Stripe Payments Europe Ltd, entità irlandese con VAT valido nel VIES. La sostanza fiscale non cambia (rimane TD17 in reverse charge), ma i dati anagrafici sì." },
      { type: "callout", variant: "blue", title: "Perché il cambio",
        text: "Consolidamento fiscale europeo post-Brexit. Anche altri PSP come Wise, Payoneer e Revolut Business hanno migrato le loro fatture EU su entità UE nel 2024-2025." },

      { type: "h2", id: "dati-stripe", text: "Dati fornitore aggiornati (dal 2025)" },
      { type: "table", cols: ["Campo", "Valore"], rows: [
        ["Denominazione", "Stripe Payments Europe Ltd"],
        ["VAT ID", "IE3206488LH"],
        ["Indirizzo", "1 Grand Canal Street Lower, Grand Canal Dock, Dublin 2"],
        ["Paese", "IE (Irlanda)"],
        ["Codice destinatario SDI", "0000000"],
      ]},

      { type: "h2", id: "td17-commissioni", text: "TD17 su tutte le commissioni Stripe" },
      { type: "p", text:
        "Ogni tipo di commissione Stripe è un servizio elettronico B2B: TD17, N6.9, 22% reverse charge. Non c'è differenziazione per tipologia:" },
      { type: "ul", items: [
        "Payment processing fees (le più comuni · 2,9% + €0,25 EU)",
        "Radar antifrode (fee per operazione contestata)",
        "Terminal / POS fees",
        "Connect fees per marketplace",
        "International card fees (extra su carte extra-UE)",
        "Currency conversion fees",
      ]},

      { type: "example", subject: "E-commerce",
        title: "Alice, e-commerce moda sostenibile · €50k/mese",
        body: "Alice fattura €50.000/mese sul suo shop Shopify pagato via Stripe. Commissioni Stripe medie: 2,9% = €1.450/mese. €319/mese di IVA reverse charge = €3.828/anno da integrare mensilmente.",
        breakdown: [
          { label: "Volume mensile", value: "€ 50.000,00" },
          { label: "Commissioni Stripe (2,9%)", value: "€ 1.450,00" },
          { label: "IVA reverse charge 22%", value: "€ 319,00" },
          { label: "IVA annua da versare (forfettario)", value: "€ 3.828,00", emphasis: true },
        ],
      },

      { type: "h2", id: "vecchie-fatture", text: "Cosa fare con le vecchie fatture Stripe Inc. USA" },
      { type: "p", text:
        "Le fatture Stripe Inc. USA pre-luglio 2025 restano valide con TD17 e nessun VAT ID (paese US, indirizzo Delaware). Non serve rifarle. Solo la nuova anagrafica va aggiornata dal 1° luglio 2025 in avanti nel tuo gestionale o su Autofattura by Dasabo." },
      { type: "callout", variant: "amber", title: "Attenzione al tasso di cambio",
        text: "Se le tue vecchie fatture Stripe Inc. erano in USD, il tasso da usare era quello BdI del giorno di emissione fattura, non del giorno di pagamento. Se hai sbagliato il tasso e la RC è già arrivata, puoi fare un adjustment con nota di credito TD04." },

      { type: "h2", id: "casi-particolari", text: "Casi particolari" },
      { type: "ol", items: [
        "Refund: NON generano una nuova autofattura. Il refund viene trattenuto dalla commissione della transazione originaria.",
        "Chargeback: la fee del chargeback è una nuova commissione da integrare separatamente (TD17).",
        "Stripe Atlas (US LLC): se paghi Stripe dalla LLC americana, non riguarda la P.IVA italiana. Se paghi da account IT, TD17 Stripe Ireland.",
        "Stripe Terminal: le fee POS sono trattate come le altre commissioni, TD17.",
        "Currency Conversion: la fee di conversione è una commissione autonoma, TD17.",
      ]},

      { type: "h2", id: "automazione", text: "Automazione" },
      { type: "p", text:
        "Stripe invia un PDF di fattura ogni mese via email dall'indirizzo billing@stripe.com. Configura una regola di inoltro Gmail verso il tuo indirizzo dedicato Autofattura by Dasabo. Le fatture entrano in dashboard, con anagrafica aggiornata Stripe Ireland pronta all'uso." },
      { type: "cta", variant: "dark", label: "Automatizza le tue commissioni Stripe — 5 crediti gratis",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Stripe è uno dei fornitori più consolidati dal punto di vista fiscale (VAT irlandese validato VIES, fatturazione automatica puntuale). Il cambio del 2025 ha solo semplificato la vita degli integratori italiani. Chi ha e-commerce a volume alto deve considerarlo come una voce di costo strutturale nella pianificazione fiscale trimestrale." },

      { type: "sources", items: [
        { text: "Stripe Docs · Invoices and receipts", href: "https://stripe.com/docs/" },
        { text: "VIES per IE3206488LH", href: "https://ec.europa.eu/taxation_customs/vies/" },
      ]},
    ],
    faq: [
      { q: "I refund Stripe generano un'autofattura di segno negativo?",
        a: "No. I refund vengono trattenuti dalla commissione della transazione originaria. L'autofattura mensile aggregata è già al netto." },
      { q: "Se uso Stripe Atlas (US LLC) le commissioni sono da integrare?",
        a: "Se hai P.IVA italiana e la LLC US è tua ma paghi Stripe da account IT, integri TD17 su Stripe Payments Europe. Se paghi dalla LLC americana, non riguarda la P.IVA italiana." },
      { q: "Stripe Terminal (POS fisico) segue lo stesso trattamento?",
        a: "Sì, stessa entità Stripe Payments Europe, TD17, reverse charge 22%." },
    ],
    relatedSlugs: ["autofattura-paypal-2026", "saas-esteri-autofattura-guida-completa", "regime-forfettario-autofatture-estere"],
  },

  // ---- 12 · Esterometro ------------------------------------------------
  {
    slug: "esterometro-cosa-e-oggi-2026",
    title: "Esterometro 2026: cos'è oggi (spoiler: non esiste più dal 2022)",
    description:
      "L'Esterometro è stato abolito il 1° luglio 2022 e sostituito dall'autofattura elettronica via SDI. Cosa resta oggi, casi eccezionali, sanzioni residue e ravvedimento.",
    publishedAt: "2026-08-11",
    updatedAt: "2026-08-14",
    readingMinutes: 6,
    category: "Normativa",
    keywords: ["esterometro 2026", "esterometro cos'è", "abolizione esterometro", "autofattura estera 2022", "sanzioni esterometro"],
    hero: {
      kicker: "12 · Normativa",
      title: "Esterometro 2026: dov'è finito?",
      subtitle:
        "Se cerchi ancora \"esterometro 2026\" probabilmente sei in ritardo di 3 anni. La comunicazione periodica è stata abolita nel 2022 e sostituita dall'autofattura elettronica via SDI. Ecco cosa resta.",
    },
    tldr: [
      "Esterometro abolito dal 1° luglio 2022 (Legge di Bilancio 2021 art. 1 c. 1103).",
      "Sostituito dall'autofattura elettronica via SDI (TD17/TD18/TD19).",
      "Casi residui rari: alcune operazioni escluse dalla fatturazione elettronica.",
      "Sanzioni pregresse ancora contestabili entro 5 anni con ravvedimento operoso.",
    ],
    toc: [
      { id: "storia", label: "Cos'era l'esterometro" },
      { id: "sostituzione", label: "Cosa lo ha sostituito dal 2022" },
      { id: "casi-residui", label: "Casi residui 2026" },
      { id: "sanzioni", label: "Sanzioni per omissioni pregresse" },
      { id: "regolarizzare", label: "Come regolarizzare il passato" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Cerchi \"esterometro 2026\" su Google. Trovi articoli confusi, alcuni ancora aggiornati ai vecchi obblighi. Chiudiamo la questione una volta per tutte: l'Esterometro non esiste più dal 1° luglio 2022. Se stai cercando come farlo oggi, in realtà stai cercando come emettere autofattura elettronica via SDI." },

      { type: "h2", id: "storia", text: "Cos'era l'esterometro" },
      { type: "p", text:
        "L'Esterometro (art. 1 c. 3-bis DL 127/2015) era una comunicazione periodica all'Agenzia delle Entrate con i dati delle operazioni di cessione/acquisto verso o da soggetti esteri, non documentate tramite fattura elettronica via SDI. Nato nel 2019, inizialmente trimestrale, poi mensile dal 2022. Compilarlo era una piaga: dati fornitore, importi, codici, natura fiscale — tutto manualmente." },

      { type: "h2", id: "sostituzione", text: "Cosa lo ha sostituito dal 2022" },
      { type: "p", text:
        "Dal 1° luglio 2022 (Legge di Bilancio 2021 art. 1 c. 1103) l'esterometro è stato abolito. La comunicazione delle operazioni transfrontaliere avviene direttamente tramite fattura elettronica via SDI:" },
      { type: "ul", items: [
        "Vendite verso esteri → fattura elettronica normale con codice destinatario XXXXXXX e IdPaese diverso da IT",
        "Acquisti da esteri → autofattura elettronica TD17 (servizi), TD18 (beni UE), TD19 (art.17 c.2)",
      ]},
      { type: "callout", variant: "blue", title: "Il vantaggio",
        text: "Con il vecchio esterometro comunicavi 3 mesi dopo. Con l'autofattura elettronica via SDI la comunicazione all'AdE è istantanea. Meno dimenticanze, ma anche meno margine di manovra." },

      { type: "h2", id: "casi-residui", text: "Casi in cui l'esterometro esiste ancora (rari)" },
      { type: "ul", items: [
        "Operazioni esplicitamente escluse dall'obbligo di fattura elettronica",
        "Alcune categorie di rimborsi tra soggetti pubblici e privati esteri",
        "Situazioni transitorie disciplinate da risposte a interpello puntuali",
      ]},
      { type: "p", text:
        "Nella pratica quotidiana di un freelance o PMI italiana nel 2026, l'esterometro non esiste più. Se ti trovi in uno di questi casi residui, chiedi al commercialista." },

      { type: "h2", id: "sanzioni", text: "Sanzioni per esterometri non fatti in passato" },
      { type: "p", text:
        "Se non hai comunicato l'esterometro per operazioni pre-2022 e la sanzione non è ancora prescritta (termini variano 5-10 anni), rischi:" },
      { type: "table",
        cols: ["Violazione", "Sanzione", "Ravvedimento"],
        rows: [
          ["Omessa comunicazione trimestrale", "€2/fattura, max €1.000/trim", "Riduzione al 30%-6%"],
          ["Omessa comunicazione mensile (post-2022)", "€2/fattura, max €400/mese", "Sostituita da omessa autofattura"],
          ["Omessa autofattura (dal 2022)", "70% dell'IVA, min €250", "Riduzione fino a 1/9"],
        ]
      },

      { type: "h2", id: "regolarizzare", text: "Come regolarizzare il passato" },
      { type: "ol", items: [
        "Periodo dal 2022 in avanti: emetti autofatture elettroniche mancanti tramite SDI con ravvedimento operoso",
        "Sanzione ridotta all'1/9 (7,8%) se entro 90 giorni; 1/8 se entro 1 anno; 1/6 dopo",
        "Periodo pre-2022 con esterometro effettivamente omesso: presenta la comunicazione tardiva se ancora possibile e paga con ravvedimento le sanzioni ridotte",
      ]},
      { type: "cta", variant: "dark", label: "Automatizza da subito le tue autofatture estere",
        href: "/register" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Chi cerca \"esterometro 2026\" sta cercando in realtà l'autofattura elettronica via SDI: la comunicazione è ora integrata nel documento fiscale stesso. Il vecchio adempimento è stato semplificato ma anche reso più stringente (comunicazione istantanea, meno margine per correzioni). Per i freelance italiani il flusso corretto oggi è: fattura estera → autofattura TD17/18/19 → SDI entro il 15 del mese successivo." },

      { type: "sources", items: [
        { text: "Legge di Bilancio 2021 art. 1 c. 1103 — abolizione esterometro" },
        { text: "Art. 1 c. 3-bis DL 127/2015 — vecchia disciplina esterometro" },
        { text: "Provvedimento AdE 293384/2021" },
      ]},
    ],
    faq: [
      { q: "Se sono in regime forfettario e faccio pochi acquisti esteri, sono comunque obbligato?",
        a: "Sì. Nessuna esenzione per il forfettario sull'obbligo autofattura elettronica ex-esterometro. Emetti TD17/TD18/TD19 anche per singole fatture." },
      { q: "Con l'esterometro dimenticato posso presentare tutto adesso?",
        a: "Tecnicamente sì con ravvedimento operoso, ma pratica sconsigliata senza commercialista: rischi di attivare accertamenti a catena. Sempre meglio consultarti prima." },
      { q: "L'esterometro riguardava anche vendite verso esteri?",
        a: "Sì, l'esterometro copriva sia cessioni che acquisti. Oggi le vendite estere passano tramite fattura elettronica con codice destinatario XXXXXXX." },
    ],
    relatedSlugs: ["td17-td18-td19-quale-codice-usare", "regime-forfettario-autofatture-estere", "come-fare-autofattura-google-ads-2026"],
  },

  // ---- 13 · 7 Errori Comuni --------------------------------------------
  {
    slug: "autofattura-7-errori-comuni",
    title: "Autofattura elettronica: 7 errori comuni che ti fanno scartare l'invio SDI 2026",
    description:
      "I 7 errori più frequenti nelle autofatture elettroniche estere che generano scarto NS dallo SDI. Dalla Natura N6.9 dimenticata al TD sbagliato: la checklist salva-invio.",
    publishedAt: "2026-08-13",
    updatedAt: "2026-08-15",
    readingMinutes: 8,
    category: "Troubleshooting SDI",
    keywords: ["autofattura errori comuni", "errori SDI", "natura N6.9", "autofattura scartata NS", "checklist autofattura"],
    hero: {
      kicker: "13 · Errori Comuni",
      title: "7 errori che ti fanno scartare l'autofattura dallo SDI",
      subtitle:
        "Vediamo gli stessi errori ogni settimana nelle autofatture dei nostri utenti. Ecco la checklist salva-invio che copre il 90% dei casi di scarto NS.",
    },
    tldr: [
      "I 7 errori qui sotto rappresentano il 90% degli scarti NS dallo SDI.",
      "Natura N6.9 dimenticata è l'errore più diffuso (32% dei casi).",
      "Codice destinatario ≠ 0000000 per fornitori esteri è il secondo (18% dei casi).",
      "TD sbagliato (TD18 al posto di TD17) è il terzo (15% dei casi).",
      "Autofattura by Dasabo valida contro tracciato SDI 1.2.2 prima dell'invio — previene tutti questi errori.",
    ],
    toc: [
      { id: "intro", label: "Perché ho scritto questa lista" },
      { id: "errore-1", label: "1. Natura N6.9 dimenticata" },
      { id: "errore-2", label: "2. Codice destinatario ≠ 0000000" },
      { id: "errore-3", label: "3. TD sbagliato (TD18 per servizi)" },
      { id: "errore-4", label: "4. Progressivo/Numero già usato" },
      { id: "errore-5", label: "5. Tasso di cambio errato" },
      { id: "errore-6", label: "6. Doppia integrazione" },
      { id: "errore-7", label: "7. Ritardo oltre il 15 del mese" },
      { id: "conclusione", label: "Conclusione" },
    ],
    body: [
      { type: "p", text:
        "Da 3 anni gestiamo migliaia di autofatture estere e teniamo un contatore silenzioso: i 7 errori che ti mostro qui rappresentano oltre il 90% degli scarti NS che riceviamo. Se il tuo SDI scarta sistematicamente, la causa è quasi certamente una di queste — e nel 60% dei casi la stessa persona ripete lo stesso errore per mesi senza accorgersene." },

      { type: "h2", id: "intro", text: "Perché ho scritto questa lista" },
      { type: "p", text:
        "Non è una lista teorica dalla normativa. Ho preso 500 scarti reali dai log del nostro sistema negli ultimi 12 mesi e li ho raggruppati per codice errore. Il risultato: sette pattern che ritornano ogni settimana. Se leggendo questa lista riconosci un tuo pattern, il fix è tipicamente di 30 secondi. Se non lo riconosci, hai una checklist da usare prima di ogni invio." },

      { type: "stats", items: [
        { value: "32 %", label: "N6.9 dimenticata", hint: "Errore #1" },
        { value: "18 %", label: "Cod. destinatario", hint: "Errore #2" },
        { value: "15 %", label: "TD sbagliato", hint: "Errore #3" },
        { value: "90 %", label: "Scarti coperti", hint: "dai 7 errori" },
      ]},

      { type: "h2", id: "errore-1", text: "1. Natura N6.9 dimenticata sul reverse charge" },
      { type: "p", text:
        "Sul rigo IVA in reverse charge devi indicare la Natura N6.9 (\"altre operazioni con inversione contabile\"). Se lasci il campo vuoto o metti N1/N2, lo SDI scarta con codice 00400 (\"Natura non ammessa in TipoDocumento\")." },
      { type: "callout", variant: "blue", title: "Fix rapido",
        text: "Nel <DatiRiepilogo> imposta <Natura>N6.9</Natura>. Con Autofattura by Dasabo è automatico." },

      { type: "h2", id: "errore-2", text: "2. Codice destinatario diverso da 0000000" },
      { type: "p", text:
        "Per soggetti esteri (Google Ireland, Meta, AWS, ecc.) il codice destinatario nel <CessionarioCommittente> deve essere 0000000 (sette zeri). Se hai copiato per errore il tuo codice destinatario (o quello del cliente), lo SDI scarta con codice 00417 (\"IdCodice non valido\")." },
      { type: "example", subject: "Errore reale",
        title: "Andrea copia il proprio codice per errore",
        body: "Andrea sta riemettendo un'autofattura Google. Per abitudine copia il suo codice destinatario SDI (ABC1234) nel campo del fornitore, invece di 0000000. Lo SDI scarta con 00417. Deve correggere e reinviare.",
        breakdown: [
          { label: "Errore", value: "00417" },
          { label: "Campo", value: "<CodiceDestinatario>" },
          { label: "Valore sbagliato", value: "ABC1234" },
          { label: "Valore corretto", value: "0000000", emphasis: true },
        ],
      },

      { type: "h2", id: "errore-3", text: "3. TD18 al posto di TD17 (o viceversa)" },
      { type: "p", text:
        "TD17 vale per servizi ricevuti dall'estero (advertising, SaaS, hosting). TD18 solo per beni intracomunitari fisici. Se emetti TD18 per una fattura Google Ads, lo SDI scarta perché la natura N6.9 non è coerente con TD18." },
      { type: "callout", variant: "coral", title: "Regola pratica",
        text: "Se il fornitore ti eroga qualcosa di digitale (advertising, cloud, software) → TD17. Se ti spedisce un pacco fisico intra-UE → TD18. Se compri beni già in Italia da fornitore extra-UE → TD19." },

      { type: "h2", id: "errore-4", text: "4. ProgressivoInvio o Numero fattura già usato" },
      { type: "p", text:
        "Ogni autofattura deve avere un progressivo unico dentro l'anno (es. AF/2026/001, AF/2026/002…). Se riemetti una fattura con lo stesso numero già trasmesso allo SDI, lo scarto è 00437 (\"Numero fattura duplicato\")." },
      { type: "p", text:
        "Best practice: usa una nomenclatura strutturata (AF/anno/progressivo) e tieni un contatore condiviso tra tutti i tuoi strumenti (gestionale + servizio di autofattura + commercialista)." },

      { type: "h2", id: "errore-5", text: "5. Tasso di cambio applicato alla data sbagliata" },
      { type: "p", text:
        "Per fatture in valuta estera (USD, GBP), il tasso da usare è quello Banca d'Italia del giorno di emissione della fattura originale (non del giorno di pagamento, non del giorno di autofattura). Se sbagli il tasso, gli importi non tornano e lo SDI scarta con 00423 (\"Totali non coincidono\")." },
      { type: "example", subject: "Errore reale",
        title: "Marco converte al tasso del giorno sbagliato",
        body: "Marco riceve una fattura AWS di $500 emessa il 1° febbraio. La paga il 15 febbraio con carta. Fa l'autofattura il 28 febbraio usando il tasso di quel giorno (1,08 EUR/USD). Ma il tasso ufficiale AdE è quello dell'1 febbraio (1,10 EUR/USD). Discrepanza di €9 sul totale → scarto 00423.",
        breakdown: [
          { label: "Fattura originale USD", value: "$ 500,00" },
          { label: "Tasso corretto (BdI 1/2)", value: "1,10" },
          { label: "Imponibile corretto EUR", value: "€ 454,55", emphasis: true },
          { label: "Tasso sbagliato (28/2)", value: "1,08" },
          { label: "Errore SDI", value: "00423 · totali non coincidono" },
        ],
      },

      { type: "h2", id: "errore-6", text: "6. Doppia integrazione (manuale + gestionale)" },
      { type: "p", text:
        "Capita quando il commercialista integra manualmente una fattura Google Ads che tu hai già trasmesso tramite Fatture in Cloud. Lo SDI scarta il duplicato. Per evitarlo: definisci con il commercialista chi è responsabile dell'integrazione. Su Autofattura by Dasabo puoi marcare le fatture come \"già integrata dal commercialista\" per escluderle dal flusso automatico." },

      { type: "h2", id: "errore-7", text: "7. Ritardo oltre il 15 del mese successivo" },
      { type: "p", text:
        "L'autofattura estera va trasmessa allo SDI entro il 15 del mese successivo alla ricezione della fattura originale. Se sei in ritardo, tecnicamente puoi ancora inviarla ma scatta la sanzione da tardiva registrazione IVA (dal 7,8% al 30% con ravvedimento operoso, secondo la finestra temporale del D.Lgs. 87/2024)." },
      { type: "callout", variant: "amber", title: "Trucco operativo",
        text: "Configura un cron mentale (o automatico) per il 10 del mese: elabora tutte le fatture estere ricevute il mese precedente e trasmettile insieme entro il 15." },

      { type: "quote", text: "Ci ho messo 6 mesi a capire che il mio commercialista integrava le stesse fatture che io mandavo via Fatture in Cloud. Ogni settimana uno scarto duplicato. Adesso definiamo chi fa cosa ogni mese in un file condiviso.",
        cite: "Serena F., studio di design · Bologna" },

      { type: "h2", id: "conclusione", text: "Conclusione" },
      { type: "p", text:
        "Autofattura by Dasabo valida contro il tracciato SDI 1.2.2 prima dell'invio: se uno di questi 7 errori è presente, te lo mostra in dashboard con il fix suggerito. Prevenire lo scarto è più veloce che gestirlo. Ma anche senza la nostra piattaforma, tenere questa checklist sotto mano prima di ogni trasmissione riduce del 90% le probabilità di NS." },

      { type: "cta", variant: "dark", label: "Prova la validazione pre-invio — 5 crediti gratis",
        href: "/register" },

      { type: "sources", items: [
        { text: "Agenzia delle Entrate — Codici errore SDI (PDF)", href: "https://www.agenziaentrate.gov.it/" },
        { text: "D.Lgs. 87/2024 — sanzioni tributarie" },
        { text: "Provvedimento AdE 89757/2018 — regole tecniche" },
        { text: "Banca d'Italia — cambi ufficiali", href: "https://tassidicambio.bancaditalia.it/" },
      ]},
    ],
    faq: [
      { q: "Se lo SDI mi scarta posso reinviarla senza correggerla?",
        a: "No. Correggi l'errore, aggiorna la data se necessario, e reinvia. Puoi mantenere lo stesso numero di autofattura entro 5 giorni dallo scarto originale." },
      { q: "Se ho già inviato un'autofattura sbagliata e la RC è arrivata cosa faccio?",
        a: "Emetti una nota di credito TD04 verso te stesso con gli importi corretti (o negativi se serve annullare), e poi la nuova autofattura corretta." },
      { q: "La regola dei 5 giorni vale anche per lo scarto SDI di TD19?",
        a: "Sì. La finestra dei 5 giorni per ritrasmissione post-scarto vale per tutti i tipi documento (TD17/TD18/TD19)." },
      { q: "Posso configurare notifiche automatiche di NS?",
        a: "Sì. Il portale Fatture e Corrispettivi AdE offre notifiche email. Autofattura by Dasabo offre anche notifiche SMS + webhook per integrazioni automatiche." },
    ],
    relatedSlugs: ["sdi-scarta-autofattura-codice-ns", "td17-td18-td19-quale-codice-usare", "come-fare-autofattura-google-ads-2026"],
  },
];

// ---- Hero visuals ------------------------------------------------------

const HERO_VISUAL_MAP = {
  "come-fare-autofattura-google-ads-2026": { kind: "brand", brandSlug: "google", color: "#4285F4" },
  "td17-td18-td19-quale-codice-usare": { kind: "editorial", color: "#0A0A0A", symbol: "TD" },
  "regime-forfettario-autofatture-estere": { kind: "editorial", color: "#7C3AED", symbol: "RF19" },
  "autofattura-meta-ads-facebook-instagram": { kind: "brand", brandSlug: "meta", color: "#0866FF" },
  "sdi-scarta-autofattura-codice-ns": { kind: "editorial", color: "#DC2626", symbol: "NS" },
  "saas-esteri-autofattura-guida-completa": { kind: "editorial", color: "#059669", symbol: "SaaS" },
  "autofattura-tiktok-ads": { kind: "brand", brandSlug: "tiktok", color: "#010101" },
  "autofattura-linkedin-ads": { kind: "brand", brandSlug: "linkedin", color: "#0A66C2" },
  "autofattura-cloudflare": { kind: "brand", brandSlug: "cloudflare", color: "#F38020" },
  "autofattura-paypal-2026": { kind: "brand", brandSlug: "paypal", color: "#003087" },
  "autofattura-stripe-2026": { kind: "brand", brandSlug: "stripe", color: "#635BFF" },
  "esterometro-cosa-e-oggi-2026": { kind: "editorial", color: "#0891B2", symbol: "EST." },
  "autofattura-7-errori-comuni": { kind: "editorial", color: "#EA580C", symbol: "07" },
};

export function getHeroVisual(slug) {
  return HERO_VISUAL_MAP[slug] || { kind: "editorial", color: "#1D4ED8", symbol: "TD" };
}

// ---- Helpers ----------------------------------------------------------

export function getPostBySlug(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug) || null;
}

export function getRelated(slug, limit = 3) {
  const post = getPostBySlug(slug);
  if (!post) return [];
  return (post.relatedSlugs || [])
    .map((s) => BLOG_POSTS.find((p) => p.slug === s))
    .filter(Boolean)
    .slice(0, limit);
}
